import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// =====================================================================
// Auth bridge
// =====================================================================
// The auth store registers callbacks here so the api module never
// imports the store directly. This avoids a module-init circular
// dependency (store -> api -> store) and keeps the api client framework-
// agnostic.

let getAuthToken: () => string | null = () => null;
let getRefreshToken: () => string | null = () => null;
let getHat: () => Hat | null = () => null;
let getImpersonationId: () => string | null = () => null;
let onTokenRefreshed: (response: LoginResponse) => void = () => {};
let onUnauthorized: () => void = () => {};
let onImpersonationEnded: () => void = () => {};
let onImpersonationForbidden: () => void = () => {};

export function registerAuthBridge(bridge: {
  getToken: () => string | null;
  /** The remembered session's `lxr_` token, or null when the user did
   *  not tick "remember me" (or the session has since ended). Always
   *  null inside a login-as session: an impersonation is never refreshed. */
  getRefreshToken: () => string | null;
  /** The hat the store is wearing (operator accounts with a home
   *  lender), so a silent refresh mints a token of the same kind. */
  getHat: () => Hat | null;
  /** The id of the admin's login-as session this tab is in, or null.
   *  Every request is stamped with it when it is sent, so that its
   *  response can be matched to the session — or the plain login — it
   *  was sent under. */
  getImpersonationId: () => string | null;
  /** A silent refresh produced a new access token — persist it together
   *  with the user block it came with (the hat may have been dropped
   *  server-side if the home lender is gone). The refresh token itself
   *  is NOT in the response and must be kept as is. */
  onTokenRefreshed: (response: LoginResponse) => void;
  onUnauthorized: () => void;
  /** A request sent inside the login-as session was refused with 401:
   *  the session is over (expired, ended elsewhere, superseded, admin
   *  demoted). The store leaves it locally and restores the admin; the
   *  SPA then goes back to where the session started. */
  onImpersonationEnded: () => void;
  /** A request sent inside the login-as session was refused with 403
   *  `impersonation_forbidden`: the session policy does not allow that
   *  (a view-only session, or one of the routes no session may call).
   *  Purely informative — the store tells the banner to say so; the
   *  request's own caller still gets the error and handles it as it
   *  always did. */
  onImpersonationForbidden: () => void;
}): void {
  getAuthToken = bridge.getToken;
  getRefreshToken = bridge.getRefreshToken;
  getHat = bridge.getHat;
  getImpersonationId = bridge.getImpersonationId;
  onTokenRefreshed = bridge.onTokenRefreshed;
  onUnauthorized = bridge.onUnauthorized;
  onImpersonationEnded = bridge.onImpersonationEnded;
  onImpersonationForbidden = bridge.onImpersonationForbidden;
}

/**
 * Request interceptor — attach the operator JWT, if any, and stamp the
 * request with the login-as session it is sent under.
 */
api.interceptors.request.use((config) => {
  const c = config as RetriableRequestConfig;
  // Stamped once: a replay keeps the stamp of the original send, which
  // is what the response interceptor compares against the store.
  if (c._sessionId === undefined) c._sessionId = getImpersonationId();
  // `_authToken` lets a caller speak with a token other than the store's
  // current one. Its only user ends a login-as session from the far side
  // of the swap back to the admin (see `endImpersonationRequest`).
  const token = c._authToken ?? getAuthToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

/**
 * Calls whose 401 means "these credentials were wrong", not "your
 * session ran out". Their pages surface the error inline, so the
 * interceptor must neither refresh nor redirect for them. `/auth/refresh`
 * is in the list for the same reason plus one more: a failed refresh
 * must never trigger another refresh.
 */
const AUTH_ENTRY_CALLS = ['/auth/login', '/auth/refresh', '/auth/debtor-login'];

/**
 * Single-flight refresh. When the access token expires, every request
 * in flight fails at once — a page load can fire ten. They all share
 * ONE call to /auth/refresh; the promise is cleared when it settles so
 * the next expiry (an hour later) starts a fresh one.
 */
let refreshInFlight: Promise<string> | null = null;

function refreshAccessToken(refreshToken: string): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = refreshSessionRequest(refreshToken, getHat())
      .then((response) => {
        onTokenRefreshed(response);
        return response.access_token;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  /** Set once the request has been re-issued after a refresh, so a
   *  second 401 on the same request is final. */
  _retried?: boolean;
  /** The login-as session (its `id`) the request was sent under, or
   *  null for none. Stamped by the request interceptor. */
  _sessionId?: string | null;
  /** Send with this token instead of the store's current one. */
  _authToken?: string;
  /** The caller reads a 401 itself: the response interceptor keeps out. */
  _skipAuthRecovery?: boolean;
};

/** What a caller may put on a request to take it out of the store's
 *  hands (see `endImpersonationRequest`). */
type CallerAuthOptions = Pick<RetriableRequestConfig, '_authToken' | '_skipAuthRecovery'>;

/**
 * A 403 `impersonation_forbidden` for a request sent inside the login-as
 * session the store is in right now: tell the store, which tells the
 * banner. Nothing is swallowed — the caller still gets the error. A 403
 * for a request of another identity (sent before the session began, or
 * under one that has ended) says nothing about this session and is left
 * alone, and so is any other 403.
 */
function noteSessionRefusal(
  error: AxiosError,
  config: RetriableRequestConfig | undefined,
): void {
  const sentUnder = config?._sessionId ?? null;
  if (sentUnder === null || sentUnder !== getImpersonationId()) return;
  // The API's envelope is `{detail: {error_code, detail}}`.
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  const code =
    typeof detail === 'object' && detail !== null && !Array.isArray(detail)
      ? (detail as { error_code?: unknown }).error_code
      : undefined;
  if (code === 'impersonation_forbidden') onImpersonationForbidden();
}

/**
 * Response interceptor — on 403 `impersonation_forbidden` inside a
 * login-as session, tell the banner (and pass the error on untouched);
 * on 401 from anything but the auth entry calls:
 *
 *  0. A request sent inside a login-as session is judged on its own
 *     terms and never reaches 1 or 2: a session has no refresh token to
 *     spend, and the session must not be replayed with — or logged out
 *     as — the admin. If the store is still in that very session it is
 *     over: the store leaves it, restores the admin and the SPA goes
 *     back to where it started (never to /login). A 401 for a request
 *     sent under a session that has since ended, or sent before one
 *     began, is stale and says nothing about the session the store is in
 *     now: it is rejected and nothing else happens.
 *  1. With a remembered session (a refresh token in the store), swap it
 *     for a new access token and re-issue the failed request ONCE. The
 *     user notices nothing.
 *  2. Otherwise — or when the refresh itself is refused, meaning the
 *     remembered session is gone too — clear the session and bounce the
 *     browser to /login, exactly as before "remember me" existed.
 *
 * We intentionally do NOT redirect for 401s coming back from the auth
 * entry calls themselves: those are wrong-credentials cases and their
 * pages surface them inline.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as RetriableRequestConfig | undefined;
    const url = config?.url ?? '';
    const isAuthEntryCall = AUTH_ENTRY_CALLS.some((path) => url.includes(path));

    if (status === 403) noteSessionRefusal(error, config);

    if (status !== 401 || isAuthEntryCall || config?._skipAuthRecovery) {
      return Promise.reject(error);
    }

    // Step 0 — sessions. `sentUnder` is what the request was stamped
    // with when it left; `inSession` is what the store is in now.
    const sentUnder = config?._sessionId ?? null;
    const inSession = getImpersonationId();
    if (sentUnder !== null) {
      if (inSession === sentUnder) onImpersonationEnded();
      return Promise.reject(error);
    }
    if (inSession !== null) {
      // Sent with the admin's own token before the session began.
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (refreshToken && config && !config._retried) {
      try {
        const accessToken = await refreshAccessToken(refreshToken);
        // A login-as session can begin while the refresh is in flight.
        // This request belongs to the admin's own login: replaying it
        // now would carry out the admin's action as the target.
        if (getImpersonationId() !== null) return Promise.reject(error);
        config._retried = true;
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
        return api.request(config);
      } catch {
        // The refresh was refused (expired, revoked, account disabled):
        // fall through to the plain sign-out below.
      }
    }

    // Same race as above, after a refresh that failed: a session that
    // began meanwhile must not be signed out for the admin's 401.
    if (getImpersonationId() !== null) return Promise.reject(error);

    onUnauthorized();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    return Promise.reject(error);
  },
);

// =====================================================================
// Auth
// =====================================================================

/**
 * Auth principal kinds returned by the backend that the SPA cares
 * about.
 *
 * - `operator` — Lexta staff. Sees every entry across every lender.
 * - `lender_human` — A human user belonging to a lender (Lendora
 *   tenant or external lender). Scoped to entries they have opened.
 * - `debtor` — A subject of one or more register entries, signed in to
 *   review their own cases. Their JWT carries `subject_key` (the
 *   SHA-256 hash of their CPR) and they can ONLY hit
 *   `GET /api/v1/debtor/cases`.
 *
 * Note: `lender_machine` (API-key bearing service principals) is also
 * a backend principal kind, but those tokens never go through the SPA
 * login flow — they're issued out-of-band — so the value is omitted
 * from this union.
 *
 * The JWT carries a matching `kind` claim plus `lender_id` (string for
 * `lender_human`, `null` otherwise) and `subject_key` (string for
 * `debtor`, `null` otherwise).
 */
export type AuthRole = 'operator' | 'lender_human' | 'debtor';

/**
 * The two hats an operator with a home lender can wear. The hat is the
 * token kind: the lender hat is a genuine `lender_human` token for the
 * home lender, the operator hat is the plain operator token. Lender
 * humans and debtors have one hat and get `null`.
 */
export type Hat = 'operator' | 'lender';

/**
 * The account behind a token, next to the EFFECTIVE `role` / `lender_id`
 * / `is_admin` the token carries. Present on every sign-in response
 * (`/auth/login`, `/auth/refresh`, `/auth/hat`) and on `/auth/me`.
 */
export interface AccountHat {
  /** `users.role` as stored; differs from `role` only under the lender hat. */
  account_role: AuthRole;
  /** `users.is_admin` as stored — labels the operator segment of the switch. */
  account_is_admin: boolean;
  /** Which hat this token wears; operator accounts only. */
  hat: Hat | null;
  /** The operator's home lender, whatever hat is worn; the switch shows iff set. */
  home_lender_id: string | null;
}

/**
 * The admin behind a login-as session ("Log ind som"): who really
 * pressed the button. Present on the `user` block of every response
 * issued inside a session, `null` everywhere else.
 */
export interface ImpersonatorInfo {
  id: string;
  email: string;
  name: string | null;
}

/**
 * A login-as session itself. `expires_at` is fixed when it starts (60
 * minutes by default) and never moves: the session has no refresh token
 * and no sliding expiry. `reason` is what the admin wrote to start it.
 */
export interface ImpersonationInfo {
  id: string;
  started_at: string;
  expires_at: string;
  reason: string;
  /**
   * A debtor or operator target: the server refuses every write in the
   * session (403 `impersonation_forbidden`), so the banner says "view
   * only". Optional only because a response from a server that predates
   * the field omits it — read as false.
   */
  view_only?: boolean;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in_seconds: number;
  user: AccountHat & {
    id: string;
    email: string;
    /** Display name; null when never set. */
    name?: string | null;
    /** Set only on a response issued inside a login-as session. */
    impersonator?: ImpersonatorInfo | null;
    /** Set only on a response issued inside a login-as session. */
    impersonation?: ImpersonationInfo | null;
    /** Effective role — what the access token carries as `kind`. */
    role: AuthRole;
    /**
     * Admin tier (operators only). Informational — every admin-only
     * endpoint re-reads the flag from the database, so editing this in
     * the browser changes what renders, never what is allowed.
     */
    is_admin: boolean;
    lender_id: string | null;
    /** SHA-256 hash of the debtor's CPR. Set only when role === 'debtor'. */
    subject_key: string | null;
    /**
     * Lender review status. Populated only for role === 'lender_human'.
     * Drives where the user lands after login.
     */
    lender_review_status: string | null;
    /**
     * Operator's free-text rejection reason copied from the lender doc.
     * Populated only when `lender_review_status === 'rejected'`. The SPA
     * surfaces this inline on the /pending-approval rejected branch so
     * the lender knows why their application was declined without a
     * second round-trip.
     */
    lender_rejection_reason: string | null;
  };
  /**
   * Opaque `lxr_` refresh token — present only when the login carried
   * `remember_me`. The store keeps it and the response interceptor
   * trades it for a new access token when the old one expires. `null`
   * on `/auth/refresh` itself (the token is not rotated) and on every
   * other sign-in path.
   */
  refresh_token: string | null;
  /** How long `refresh_token` stays redeemable, in seconds (30 days). */
  refresh_expires_in_seconds: number | null;
}

export async function loginRequest(
  email: string,
  password: string,
  rememberMe = false,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/login', {
    email,
    password,
    remember_me: rememberMe,
  });
  return data;
}

/**
 * Swap a remembered session's refresh token for a fresh access JWT.
 * Called by the response interceptor, never by pages. A 401 here means
 * the remembered session is over (expired, revoked, account disabled).
 */
export async function refreshSessionRequest(
  refreshToken: string,
  hat: Hat | null = null,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/refresh', {
    refresh_token: refreshToken,
    ...(hat ? { hat } : {}),
  });
  return data;
}

/**
 * Put on the other hat. Only an operator account with a home lender can
 * (403 `hat_unavailable` otherwise). Returns a full sign-in response —
 * a fresh access token of the requested kind plus the user block — with
 * `refresh_token: null`: the remembered session is untouched.
 */
export async function switchHatRequest(hat: Hat): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/hat', { hat });
  return data;
}

/**
 * Tell the backend the user signed out. Without a refresh token this is
 * a formality (the access JWT is dropped client-side); with one it ends
 * the remembered session server-side so no device can silently mint
 * another access token under it.
 */
export async function logoutRequest(
  refreshToken?: string | null,
): Promise<void> {
  await api.post(
    '/auth/logout',
    refreshToken ? { refresh_token: refreshToken } : undefined,
  );
}

// ---------------------------------------------------------------------
// Login-as ("Log ind som") — admin impersonation
// ---------------------------------------------------------------------
//
// See docs/superpowers/specs/2026-09-30-admin-impersonation-design.md.
//   POST /auth/impersonation      — admin only; a session token for the
//                                   target, never a refresh token
//   POST /auth/impersonation/end  — ends the session. With the session's
//                                   own token that session (the body is
//                                   ignored); with the ADMIN's token only
//                                   the session whose id the body names
//                                   (`{"impersonation_id"}`, 422
//                                   `impersonation_id_required` without)
//   GET  /entries/{id}/debtor-account — admin only; finds the debtor's login

/**
 * Start a login-as session as `userId`. `reason` (3–500 characters) is
 * stored with the session and is what makes an action taken on the
 * target's behalf defensible afterwards. Returns the target's own
 * sign-in response — `refresh_token` null, `user.impersonator` /
 * `user.impersonation` set. Refusals carry an `error_code`:
 * `impersonation_forbidden`, `operator_required`, `admin_required`,
 * `too_many_impersonations` (429), `invalid_user_id`,
 * `cannot_impersonate_self`, `user_not_found`, `target_not_impersonable`,
 * `cannot_impersonate_admin`, `target_inactive`, `target_misconfigured`;
 * a reason outside 3–500 characters is a 422.
 */
export async function startImpersonationRequest(
  userId: string,
  reason: string,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/impersonation', {
    user_id: userId,
    reason,
  });
  return data;
}

/**
 * End a login-as session, speaking with `token`. Never the store's own:
 * which kind of token the store holds changes under a caller's feet (the
 * swap back to the admin happens BEFORE the server hears about the end),
 * so every caller says whose token it means.
 *
 *  * `token` is the SESSION's own: the server ends that session; the
 *    body is ignored.
 *  * `token` is the ADMIN's own: the server ends only the session whose
 *    id the body names, and answers 422 `impersonation_id_required`
 *    without one — so a stale tab's clean-up can never end a newer
 *    session. Every admin-token call therefore carries the id.
 *
 * The id is sent with both kinds. `null` exists for exactly one caller:
 * a start response too malformed to name its session, closed again with
 * its own token (which needs no id). `ended` is false when there was
 * nothing to end. The call is not retried, refreshed or turned into a
 * sign-out if the server refuses it: the caller reads the failure.
 */
export async function endImpersonationRequest(
  impersonationId: string | null,
  token: string,
): Promise<{ ended: boolean }> {
  const options: AxiosRequestConfig & CallerAuthOptions = {
    _authToken: token,
    _skipAuthRecovery: true,
  };
  const { data } = await api.post<{ ended: boolean }>(
    '/auth/impersonation/end',
    impersonationId ? { impersonation_id: impersonationId } : undefined,
    options,
  );
  return data;
}

/** The debtor's own login for an entry's subject (role `debtor`). */
export interface EntryDebtorAccount {
  user_id: string;
  created_at: string | null;
  last_login_at: string | null;
}

/**
 * Admin only: the debtor login that belongs to an entry's subject, or
 * `null` when the debtor has never signed in (the account is created on
 * their first login; nobody creates one on their behalf).
 */
export async function getEntryDebtorAccount(
  entryId: string,
): Promise<EntryDebtorAccount | null> {
  try {
    const { data } = await api.get<EntryDebtorAccount>(
      `/entries/${encodeURIComponent(entryId)}/debtor-account`,
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

// =====================================================================
// Password reset
// =====================================================================
//
// Three endpoints under `/auth/password-reset/*`. See
// `docs/reset-password-design.md` for the full design.
//
//   POST /auth/password-reset/request   — public; always 202 + ok=true
//                                         regardless of whether the
//                                         email exists. The email
//                                         (with reset link) is sent
//                                         only to eligible operator /
//                                         lender_human users.
//   GET  /auth/password-reset/{t}/validate — public; the
//                                         complete-reset page calls
//                                         this on mount to confirm
//                                         the link is still good.
//   POST /auth/password-reset/complete  — public; exchanges a token +
//                                         a new password for an auto-
//                                         sign-in JWT (LoginResponse
//                                         shape).

export interface RequestResetResponse {
  ok: boolean;
}

/**
 * ``language`` is the SPA's currently-selected i18next locale
 * (``da`` or ``en``). The backend renders the reset email in that
 * language. Omit / pass ``undefined`` and the backend falls back
 * to Danish.
 */
export async function requestPasswordReset(
  email: string,
  language?: 'da' | 'en',
): Promise<RequestResetResponse> {
  const { data } = await api.post<RequestResetResponse>(
    '/auth/password-reset/request',
    { email, language },
  );
  return data;
}

export interface ValidateResetResponse {
  email: string;
  expires_at: string;
}

export async function validateResetToken(
  token: string,
): Promise<ValidateResetResponse> {
  const { data } = await api.get<ValidateResetResponse>(
    `/auth/password-reset/${encodeURIComponent(token)}/validate`,
  );
  return data;
}

/**
 * Complete the reset and auto-sign-in. The backend reuses the
 * `AcceptInviteResponse` shape: `{access_token, token_type,
 * expires_in_seconds, user: dict}`. We narrow `user` to the same
 * fields LoginResponse exposes so the SPA can drop the body
 * straight into `useAuthStore.setSession()`.
 */
export interface CompleteResetResponse {
  access_token: string;
  token_type: string;
  expires_in_seconds: number;
  user: {
    id: string;
    email: string;
    role: AuthRole;
    lender_id: string | null;
    subject_key: string | null;
  };
}

export async function completePasswordReset(
  token: string,
  newPassword: string,
): Promise<CompleteResetResponse> {
  const { data } = await api.post<CompleteResetResponse>(
    '/auth/password-reset/complete',
    { token, new_password: newPassword },
  );
  return data;
}

// =====================================================================
// Debtor self-service login
// =====================================================================
//
// Debtors don't have email + password — they auth with `case_number
// + CPR`. See `backend/app/api/v1/endpoints/debtor_login.py` for the
// full design. The response shape matches `LoginResponse` so the SPA
// can drop it into `useAuthStore.setSession()`.

export interface DebtorLoginResponse {
  access_token: string;
  token_type: string;
  expires_in_seconds: number;
  user: {
    id: string;
    email: string;
    role: AuthRole;
    lender_id: string | null;
    subject_key: string | null;
  };
}

export async function debtorLogin(
  caseNumber: string,
  number: string,
  country: IdCountry = 'DK',
): Promise<DebtorLoginResponse> {
  const body =
    country === 'DK'
      ? { case_number: caseNumber, cpr: number }
      : { case_number: caseNumber, national_id: number, id_country: country };
  const { data } = await api.post<DebtorLoginResponse>('/auth/debtor-login', body);
  return data;
}

/**
 * Shape of `GET /api/v1/auth/me`. Mirrors the backend `MeResponse`
 * exactly — `tsc --noEmit` is the contract here. Used by the
 * /pending-approval page to poll for fresh review state without
 * forcing the lender to sign out and back in.
 */
export interface MeResponse extends AccountHat {
  id: string;
  email: string;
  /** Effective role — follows the presenting token's kind (see `AccountHat`). */
  role: AuthRole;
  /** Display name; null when never set. */
  name?: string | null;
  /** Set only while the presenting token is a login-as session's. */
  impersonator?: ImpersonatorInfo | null;
  impersonation?: ImpersonationInfo | null;
  is_active: boolean;
  /** Admin tier (operators only). Informational — see `LoginResponse`. */
  is_admin: boolean;
  lender_id: string | null;
  subject_key: string | null;
  lender_review_status: string | null;
  /** Set only when `lender_review_status === 'rejected'`. */
  lender_rejection_reason: string | null;
  created_at: string | null;
  last_login_at: string | null;
}

/**
 * Refresh the signed-in human's record. The /pending-approval page
 * polls this so an approved lender sees the upgrade without a manual
 * refresh; other pages can call it to confirm the cached login-time
 * state isn't stale.
 */
export async function getMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/auth/me');
  return data;
}

// =====================================================================
// Lender invites (operator-issued, recipient-accepted)
// =====================================================================
//
// Three endpoints, see `backend/app/api/v1/endpoints/invites.py`:
//   POST /invites               — operator (any lender, or a new
//                                 operator) / approved lender admin (own
//                                 lender) creates a one-shot invite,
//                                 token returned ONCE in `accept_url`.
//   GET  /invites/{token}/preview — public; the SignupPage hits this
//                                 on mount to confirm "you're being
//                                 invited to <Lender X> as <email>".
//   POST /invites/accept        — public; exchanges a still-valid
//                                 token + chosen password for a fresh
//                                 lender_human (or operator) user + an
//                                 auto-sign-in JWT (same shape as
//                                 /auth/login).
//
// Invite tokens are NOT JWTs — they're 32-byte URL-safe-b64 strings
// only ever known in plaintext by (a) the operator who just minted
// them, (b) the recipient via the email link. The DB only stores
// SHA-256 hashes. The SPA never logs the token.

/**
 * Which kind of user an invite mints on accept. `lender_human` (the
 * default) binds to a `lender_id`; `operator` is Lexta staff with no
 * lender binding and can ONLY be issued by an existing operator.
 */
export type InviteRole = 'lender_human' | 'operator';

export interface CreateInvitePayload {
  /** Defaults to `lender_human` server-side when omitted. */
  role?: InviteRole;
  /** Required for `lender_human`, must be absent for `operator`. */
  lender_id?: string;
  email: string;
  ttl_days?: number;
}

export interface CreateInviteResponse {
  invite_id: string;
  token: string;
  accept_url: string;
  expires_at: string;
  role: InviteRole;
  /** `null` for operator invites. */
  lender_id: string | null;
  /** `null` for operator invites. */
  lender_display_name: string | null;
  email: string;
}

export interface InvitePreview {
  email: string;
  role: InviteRole;
  /** `null` for operator invites. */
  lender_id: string | null;
  /** `null` for operator invites. */
  lender_display_name: string | null;
  expires_at: string;
}

export interface AcceptInvitePayload {
  token: string;
  password: string;
  name?: string;
}

export async function createInvite(
  payload: CreateInvitePayload,
): Promise<CreateInviteResponse> {
  const { data } = await api.post<CreateInviteResponse>('/invites', payload);
  return data;
}

export async function getInvitePreview(token: string): Promise<InvitePreview> {
  const { data } = await api.get<InvitePreview>(
    `/invites/${encodeURIComponent(token)}/preview`,
  );
  return data;
}

export async function acceptInvite(
  payload: AcceptInvitePayload,
): Promise<LoginResponse> {
  // The accept endpoint returns the same shape as /auth/login —
  // reuse `LoginResponse` so the SPA can drop the body straight into
  // `useAuthStore` without a second mapping layer.
  const { data } = await api.post<LoginResponse>('/invites/accept', payload);
  return data;
}

// =====================================================================
// Public creditor self-signup + operator review console
// =====================================================================
//
// Four endpoints, see `backend/app/api/v1/endpoints/creditor_signup.py`:
//   POST /creditor-signup           — public; creates a lender row in
//                                    `review_status: "pending_review"`
//                                    + an admin lender_human user; returns
//                                    an auto-sign-in JWT.
//   GET  /lenders/pending           — operator-only; queue of lenders
//                                    awaiting operator review.
//   POST /lenders/{id}/approve      — operator-only; flips to
//                                    `review_status: "approved"` +
//                                    `is_active: true`.
//   POST /lenders/{id}/reject       — operator-only; flips to
//                                    `review_status: "rejected"`,
//                                    keeps `is_active: false`.
//
// A self-signed lender_human can sign in (so they can see the
// "your account is awaiting approval" UI) but the register-write
// path refuses them with `error_code: "lender_pending_approval"`
// until an operator approves the lender.

export interface CreditorSignupPayload {
  display_name: string;
  contact_email: string;
  contact_phone?: string;
  admin_name?: string;
  admin_password: string;
}

export interface CreditorSignupLender {
  id: string;
  display_name: string;
  /** "pending_review" on a fresh signup; the operator console flips it
   *  to "approved" or "rejected". Echoed back so the SPA can render
   *  the right post-signup banner without polling. */
  review_status: string;
}

export interface CreditorSignupResponse extends LoginResponse {
  lender: CreditorSignupLender;
}

export async function signupCreditor(
  payload: CreditorSignupPayload,
): Promise<CreditorSignupResponse> {
  const { data } = await api.post<CreditorSignupResponse>(
    '/creditor-signup',
    payload,
  );
  return data;
}

export interface PendingLender {
  id: string;
  display_name: string;
  contact_email: string;
  contact_phone: string | null;
  /** Email of the admin lender_human that self-signed alongside this
   *  lender. The operator uses this to recognise "is this the same
   *  person that emailed me asking to be onboarded". */
  admin_user_email: string | null;
  submitted_at: string;
  review_status: string;
}

export async function listPendingLenders(): Promise<PendingLender[]> {
  const { data } = await api.get<PendingLender[]>('/lenders/pending');
  return data;
}

interface LenderReviewActionResponse {
  id: string;
  review_status: string;
  is_active: boolean;
}

export async function approveLender(
  id: string,
  note?: string,
): Promise<LenderReviewActionResponse> {
  const { data } = await api.post<LenderReviewActionResponse>(
    `/lenders/${encodeURIComponent(id)}/approve`,
    { note: note ?? null },
  );
  return data;
}

export async function rejectLender(
  id: string,
  reason?: string,
): Promise<LenderReviewActionResponse> {
  const { data } = await api.post<LenderReviewActionResponse>(
    `/lenders/${encodeURIComponent(id)}/reject`,
    { reason: reason ?? null },
  );
  return data;
}

// =====================================================================
// Lookup
// =====================================================================

export interface LookupHit {
  entry_id: string;
  /** Which matched subject this hit belongs to. */
  subject_key: string | null;
  originating_lender_name: string;
  debt_amount: number;
  currency: string;
  opened_at: string;
  last_payment_activity_at: string | null;
  has_active_dispute: boolean;
  /** True when the underlying entry was filed without CPR.
   *  The Verdict UI uses this to render a "Mangelfuld" badge per hit.
   *  Null on legacy hits served before the backend exposed the field. */
  is_incomplete: boolean | null;
}

export type LookupQueryType =
  | 'cpr'
  | 'national_id'
  | 'facebook_url'
  | 'name'
  | 'phone';

/**
 * One matched subject with its own verdict. A name lookup can resolve
 * to several distinct people; the SPA renders one group per subject so
 * the lender can tell them apart. `facebook_url` is the only
 * identifying detail beyond the debt entries.
 */
export interface LookupSubjectGroup {
  subject_key: string;
  posture: 'hard_block' | 'soft_warn' | 'clear';
  hit_count: number;
  total_debt: number;
  /** Per-currency totals; `total_debt` is the legacy blind sum. */
  total_debt_by_currency?: Record<string, number>;
  is_incomplete: boolean;
  facebook_url: string | null;
  /** ISO date. Same disclosure rule as `facebook_url`: name lookups by
   *  human principals only. */
  birth_date?: string | null;
  hits: LookupHit[];
}

export interface LookupResponse {
  /** First matched subject key — for the CPR + FB-URL paths this is
   *  always a single subject; for the name path it is the first match
   *  out of `subject_keys`. Empty string when nothing matched. */
  subject_key: string;
  /** Every subject key that matched the query. CPR + FB-URL lookups
   *  always resolve to ≤ 1 subject; name lookup may resolve to several
   *  when multiple distinct CPR-keyed subjects share a display name. */
  subject_keys: string[];
  query_type: LookupQueryType;
  /** Country of the identity number the lookup was keyed on, when it was. */
  id_country?: string | null;
  posture: 'hard_block' | 'soft_warn' | 'clear';
  hit_count: number;
  total_debt: number;
  /** Per-currency totals; `total_debt` is the legacy blind sum. */
  total_debt_by_currency?: Record<string, number>;
  hits: LookupHit[];
  /** Same hits regrouped per matched subject, each with its own verdict. */
  subjects: LookupSubjectGroup[];
}

export type LookupQuery =
  | { type: 'cpr'; value: string; country?: IdCountry }
  | { type: 'facebook_url'; value: string }
  | { type: 'name'; value: string; birthDate?: string }
  | { type: 'phone'; value: string };

export async function lookup(query: LookupQuery): Promise<LookupResponse> {
  // Build the payload as a single field per query kind. The backend's
  // root validator refuses bodies with more than one of {cpr,
  // facebook_url, name}, so we deliberately omit the unused keys
  // rather than send them as ``null``.
  const payload: Record<string, string> = {};
  if (query.type === 'cpr' && query.country && query.country !== 'DK') {
    // Non-Danish identity numbers travel as national_id + id_country.
    payload.national_id = query.value;
    payload.id_country = query.country;
  } else {
    payload[query.type] = query.value;
  }
  // Name lookups may carry a birth date to narrow same-name subjects.
  // Subjects with no birth date on file are still returned.
  if (query.type === 'name' && query.birthDate) {
    payload.birth_date = query.birthDate;
  }
  const { data } = await api.post<LookupResponse>('/lookup', payload);
  return data;
}

// =====================================================================
// Register entries
// =====================================================================

/**
 * Where the originating loan came from. The SPA picks one of these from
 * a select; the backend validates it against the same Literal and stamps
 * it on the register entry. The set names the *origination channel* of
 * the underlying loan rather than the platform that filed it — Lexta
 * itself is product-agnostic.
 *
 * Must stay in lock-step with `OriginatedFrom` in
 * `backend/app/schemas/register.py`.
 */
export type OriginatedFrom =
  | 'lenderly_private'
  | 'facebook_private'
  | 'p2p_platform'
  | 'other';

/**
 * Document type tags. The backend uses canonical English-snake_case
 * tokens (`health_insurance | passport | borger_dk | bank_transfer |
 * other`); the SPA presents them through localized labels via
 * `openEntry.documentType_*` i18n keys.
 */
export type DocumentType =
  // Country-neutral set offered for new uploads.
  | 'id_document'
  | 'address_proof'
  | 'bank_transfer'
  | 'loan_agreement'
  | 'income_proof'
  | 'correspondence'
  | 'other'
  // Legacy values still on stored entries; rendered, not offered.
  | 'health_insurance'
  | 'passport'
  | 'borger_dk';

/**
 * Per-document record attached to a register entry. After the upload
 * milestone, `url` is the real CDN URL returned by
 * `POST /uploads/document`; only `type | filename | url` are persisted
 * on the entry.
 */
export interface RegisterDocument {
  type: DocumentType;
  filename: string;
  url: string;
}

/**
 * Response shape from `POST /api/v1/uploads/document`. The SPA tracks
 * `document_id` + `size_bytes` per row for the inline preview but only
 * forwards `type | filename | url` to `/register/entries`.
 */
export interface UploadedDocument {
  document_id: string;
  filename: string;
  url: string;
  doc_type: DocumentType;
  size_bytes: number;
}

/**
 * Upload a single document blob. Reuses the shared axios client so the
 * auth interceptor attaches the JWT. We override the per-request
 * `Content-Type` so axios sets the multipart boundary from the
 * `FormData` — the client default is `application/json`.
 */
export async function uploadDocument(
  file: File,
  docType: DocumentType,
): Promise<UploadedDocument> {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_type', docType);
  const { data } = await api.post<UploadedDocument>(
    '/uploads/document',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Snapshot of who filed the entry, captured by the backend from the
 * auth context. The SPA never sends this — it only renders it from
 * the response.
 */
export interface CreditorContact {
  name: string;
  phone?: string | null;
  email: string;
}

export interface RegisterEntry {
  id: string;
  subject_key: string;
  /** Nullable under the minimum-set rule — entries filed with CPR
   *  alone, mobile alone, or FB URL alone carry no `subject_name`.
   *  Such entries always lookup as `soft_warn` and are flagged
   *  `is_incomplete=true`. */
  subject_name: string | null;
  subject_address: string | null;
  /** Optional debtor contact fields — captured on the open-entry form
   *  and snapshotted on the entry. Each is independently nullable so
   *  partial population is fine. */
  subject_phone: string | null;
  subject_email: string | null;
  subject_postal_code: string | null;
  subject_city: string | null;
  subject_facebook_url: string | null;
  /** Soft identifier (ISO date). Derived from the CPR when filed with one. */
  subject_birth_date?: string | null;
  /** Country of the identity number the entry is keyed on; DK for
   *  CPR-keyed rows, null while the entry is CPR-less. */
  id_country?: string | null;
  originating_lender_id: string;
  originated_from: OriginatedFrom;
  debt_amount: number;
  principal_amount: number;
  /** Late fees / interest / collection costs added on top of the
   *  principal. `null` on entries filed before the split existed —
   *  render as "not itemised", never as 0. */
  fees_amount: number | null;
  repaid_amount: number;
  /** Disbursement date (`YYYY-MM-DD`). Optional on reads because
   *  pre-this-change entries don't have the field. */
  loan_disbursed_at: string | null;
  /** Date the borrower last missed an expected payment, i.e. when
   *  default began (`YYYY-MM-DD`). Optional on reads — legacy entries
   *  filed before this field existed don't carry the value. */
  default_since: string | null;
  currency: string;
  status: 'active' | 'closed' | 'auto_expired';
  opened_at: string;
  last_payment_activity_at: string | null;
  closed_at: string | null;
  auto_expired_at: string | null;
  dispute_state: string | null;
  history_notes: string | null;
  /** True when the entry was filed without CPR (mobile/FB/name+address
   *  alone). Such entries always surface as soft_warn on lookup —
   *  see backend `is_incomplete` on RegisterEntryResponse. Null on
   *  legacy entries written before this field existed. */
  is_incomplete: boolean | null;
  documents: RegisterDocument[];
  creditor_contact: CreditorContact;
  payment_destination: string | null;
  /** Standing consent to a fixed-price sale to Lexta, or null. */
  auto_sale_consent?: AutoSaleConsent | null;
  /** ADMIN-INDGREB flags, as on `InventoryEntry`. */
  opened_admin_intervention?: boolean;
  amounts_admin_intervention?: boolean;
  payment_instruction_admin_intervention?: boolean;
  consent_withdrawn_admin_intervention?: boolean;
  close_admin_intervention?: boolean;
}

/** Currencies a claim can be filed in. Every amount on an entry is in
 *  the entry's currency; the register never converts. */
/** Countries whose national identity numbers the register can key on.
 *  Which of them a lender may use comes from `GET /public/id-countries`
 *  (the `ENABLED_ID_COUNTRIES` platform setting). */
export type IdCountry = 'DK' | 'SE' | 'NO';
export const ID_COUNTRIES: readonly IdCountry[] = ['DK', 'SE', 'NO'];

export interface IdCountriesResponse {
  supported: IdCountry[];
  enabled: IdCountry[];
  labels: Record<string, string>;
}

/** Public, unauthenticated — the debtor login page needs it too. */
export async function getIdCountries(): Promise<IdCountriesResponse> {
  const { data } = await api.get<IdCountriesResponse>('/public/id-countries');
  return data;
}

export type Currency = 'DKK' | 'NOK' | 'SEK' | 'EUR';
export const CURRENCIES: readonly Currency[] = ['DKK', 'NOK', 'SEK', 'EUR'];

export interface OpenEntryPayload {
  /** ISO 4217 code. DKK when omitted. */
  currency?: Currency;
  /** Optional under the minimum-set rule: an entry may be filed
   *  without CPR if at least one alternative identifier (mobile, FB
   *  URL, or name+address) is present. The backend enforces the
   *  minimum-set rule; the SPA mirrors it client-side via
   *  `validateMinimumSet` (Task 8). Entries filed without CPR are
   *  flagged `is_incomplete=true` and always lookup as `soft_warn`. */
  cpr?: string;
  /** General form of the identity number; `cpr` is the Danish shorthand.
   *  Never send both. */
  id_country?: IdCountry;
  national_id?: string;
  /** Optional under the minimum-set rule (see `cpr` above). May be
   *  omitted when CPR / mobile / FB URL alone provides identification. */
  subject_name?: string;
  subject_address?: string;
  /** Optional debtor contact fields. The form sends `undefined` for
   *  empty inputs (matching the existing `subject_address` pattern) so
   *  the backend's `max_length` validators only see real values. */
  subject_phone?: string;
  subject_email?: string;
  subject_postal_code?: string;
  subject_city?: string;
  subject_facebook_url?: string;
  /** ISO date or the first six CPR digits; the backend normalises. */
  subject_birth_date?: string;
  principal_amount: number;
  repaid_amount: number;
  /** Current amount owed as stated by the lender ("Aktuelt skyldigt
   *  beløb") — today's claim after payments, interest / fees / costs
   *  included. Required. The backend derives `fees_amount` (added after
   *  default) as debt + repaid − principal. */
  debt_amount: number;
  /**
   * Last date the loan amount was disbursed / transferred to the
   * borrower, in `YYYY-MM-DD` form. The backend rejects anything
   * younger than 6 weeks (Danish creditor-law convention — a debt
   * isn't formally in default until ~6 weeks after the disbursement /
   * agreed-repayment date).
   */
  loan_disbursed_at: string;
  /**
   * Date the borrower last missed an expected payment (i.e. when
   * default began), in `YYYY-MM-DD` form. NOT the loan-disbursement
   * date. Required on every new submission. For manual filings
   * (lender_human / operator) the backend rejects anything younger
   * than 6 weeks (42 days) — Danish creditor convention. API-key
   * (lender_machine) integrations are exempt; their integrating
   * system handles its own dispatch timing.
   */
  default_since: string;
  originated_from: OriginatedFrom;
  history_notes?: string;
  documents: RegisterDocument[];
  payment_destination?: string;
  /** One of the filing lender's payment methods (`LenderListItem.
   *  payment_methods`); the backend stores its text as
   *  `payment_destination`. Never together with `payment_destination`. */
  payment_method_id?: string;
  /**
   * Operator-only override: the lender record this entry is filed on
   * behalf of. The backend snapshots the lender's contact on the entry
   * and stamps `originating_lender_id` from this. For `lender_human`
   * principals the backend resolves the lender from the JWT and the
   * SPA must NOT send this field.
   */
  acting_as_lender_id?: string;
  /** Standing consent to a fixed-price sale to Lexta — see `AutoSaleConsent`. */
  auto_sale_consent?: boolean;
}

export async function openEntry(
  payload: OpenEntryPayload,
): Promise<RegisterEntry> {
  const { data } = await api.post<RegisterEntry>('/register/entries', payload);
  return data;
}

/**
 * Per-lender policy override values. `null` falls back to the
 * platform default (`DEFAULT_LENDER_POSTURE`); `'hard_block'` /
 * `'soft_warn'` force that posture for hits originating from this
 * lender. `'clear'` is intentionally NOT a valid override — a lender
 * can't suppress visibility of its own entries.
 */
export type LenderPolicyOverride = 'hard_block' | 'soft_warn' | null;

/** A lender row is either a `creditor` (files its own claims) or a
 *  `platform` (files on behalf of the real creditor via `on_behalf_of`
 *  — see the creditor-attribution design doc). Defaults to `creditor`
 *  server-side; only an admin may change it (`setLenderKind`). */
export type LenderKind = 'creditor' | 'platform';

/**
 * Lender directory entry returned by `GET /api/v1/lenders` (operator
 * only). Used by the OpenEntryPage operator picker so the operator can
 * pick which lender they're filing on behalf of, and by the
 * `/team/lenders` console as the row source.
 */
export interface LenderListItem {
  id: string;
  display_name: string;
  /** `null` when the lender row carries neither a `contact_email` nor
   *  an `email` to fall back on — the backend's `LenderSummary` types
   *  this `Optional[str]`, so the SPA must handle the absence. */
  contact_email: string | null;
  contact_phone: string | null;
  posture: string;
  /** Per-lender override of the platform-default lookup posture.
   *  `null` means "use the platform default". */
  policy_override: LenderPolicyOverride;
  /** Onboarding state — surfaced so the operator console can render a
   *  status badge alongside the policy controls. Values come from the
   *  creditor-signup flow: `approved` / `pending_review` / `rejected`,
   *  or `null` for legacy rows. */
  review_status: string | null;
  kind: LenderKind;
  /** Where the lender's debtors may pay ("Betalingsmuligheder"). */
  payment_methods: PaymentMethod[];
  /** The method the filing form preselects; `null` only when there are none. */
  default_payment_method_id: string | null;
}

/** A payment type's id — one of the platform's list (`getPaymentMethodTypes`,
 *  edited under Indstillinger → Betalingstyper), or `other` for free text. */
export type PaymentMethodKind = string;

/** The free-text type, always offered besides the platform's list. */
export const OTHER_PAYMENT_KIND = 'other';

/** One entry of the platform's payment type list. */
export interface PaymentMethodType {
  id: string;
  /** Shown in the dropdown and to the debtor before the details. */
  name: string;
  /** Example text for the empty details field. */
  hint: string | null;
}

/** A row of the administrator's list; no `id` = a new type. */
export interface PaymentMethodTypeInput {
  id?: string;
  name: string;
  hint?: string | null;
}

export async function getPaymentMethodTypes(): Promise<PaymentMethodType[]> {
  const { data } = await api.get<{ types: PaymentMethodType[] }>('/payment-method-types');
  return data.types;
}

/** One of a lender's payment methods. */
export interface PaymentMethod {
  id: string;
  kind: PaymentMethodKind;
  /** The type's name as this method shows it ("Wise"); `null` = other. */
  kind_name: string | null;
  /** Account number, phone number, PayPal address … — what the debtor needs. */
  details: string;
  /** The lender's own name for it; never shown to debtors. */
  label: string | null;
  /** The exact text an entry stores (and its debtor sees) when this is picked. */
  destination: string;
  is_default: boolean;
  /** ADMIN-INDGREB: kind/details were last written by a Lexta admin
   *  logged in as one of the lender's users. */
  admin_intervention: boolean;
}

export interface PaymentMethodInput {
  kind: PaymentMethodKind;
  details: string;
  label?: string | null;
  make_default?: boolean;
}

/** Operator, or the lender's own users. Returns the updated lender. */
export async function addPaymentMethod(
  lenderId: string,
  method: PaymentMethodInput,
): Promise<LenderListItem> {
  const { data } = await api.post<LenderListItem>(
    `/lenders/${encodeURIComponent(lenderId)}/payment-methods`,
    method,
  );
  return data;
}

/** `label: ""` clears the label; `make_default: true` moves the default here. */
export async function updatePaymentMethod(
  lenderId: string,
  methodId: string,
  changes: { kind?: PaymentMethodKind; details?: string; label?: string; make_default?: true },
): Promise<LenderListItem> {
  const { data } = await api.patch<LenderListItem>(
    `/lenders/${encodeURIComponent(lenderId)}/payment-methods/${encodeURIComponent(methodId)}`,
    changes,
  );
  return data;
}

/** Entries already pointing to the method keep their text. */
export async function removePaymentMethod(
  lenderId: string,
  methodId: string,
): Promise<LenderListItem> {
  const { data } = await api.delete<LenderListItem>(
    `/lenders/${encodeURIComponent(lenderId)}/payment-methods/${encodeURIComponent(methodId)}`,
  );
  return data;
}

/** The payment methods an entry may point its debtor to: those of the
 *  lender the debtor pays (the owner, or the seller while Lexta collects
 *  through the seller's account). */
export interface EntryPaymentMethods {
  payee_lender_id: string;
  payee_name: string;
  current_destination: string | null;
  methods: PaymentMethod[];
}

export async function getEntryPaymentMethods(entryId: string): Promise<EntryPaymentMethods> {
  const { data } = await api.get<EntryPaymentMethods>(
    `/register/entries/${encodeURIComponent(entryId)}/payment-methods`,
  );
  return data;
}

export async function getLenders(): Promise<LenderListItem[]> {
  const { data } = await api.get<LenderListItem[]>('/lenders');
  return data;
}

/**
 * Admin-only: flip a lender between `creditor` and `platform`. Only
 * `platform` lenders may send `on_behalf_of` when filing entries.
 * Audits `lender.kind_changed`.
 */
export async function setLenderKind(
  lenderId: string,
  kind: LenderKind,
): Promise<LenderListItem> {
  const { data } = await api.put<LenderListItem>(
    `/lenders/${encodeURIComponent(lenderId)}/kind`,
    { kind },
  );
  return data;
}

/**
 * Operator-only: set or clear a single lender's `policy_override`.
 * Pass `null` to clear (lender falls back to the platform default).
 * The backend rejects any value other than `'hard_block'` /
 * `'soft_warn'` / `null` with 422.
 */
export async function setLenderPolicy(
  lenderId: string,
  policy: LenderPolicyOverride,
): Promise<LenderListItem> {
  const { data } = await api.put<LenderListItem>(
    `/lenders/${encodeURIComponent(lenderId)}/policy`,
    { policy_override: policy },
  );
  return data;
}

/** Operator: edit a lender's display name and contact details. `""` for
 *  the phone clears it. The identity e-mail is not editable here. */
export async function updateLenderInfo(
  lenderId: string,
  changes: { display_name?: string; contact_email?: string; contact_phone?: string },
): Promise<LenderListItem> {
  const { data } = await api.patch<LenderListItem>(
    `/lenders/${encodeURIComponent(lenderId)}`,
    changes,
  );
  return data;
}

/** Self-service: edit your own display name. The login e-mail is the
 *  account key and is not editable here (the backend refuses it). */
export async function updateMyProfile(name: string): Promise<MeResponse> {
  const { data } = await api.patch<MeResponse>('/auth/me', { name });
  return data;
}

/** Self-service password change; 403 when the current password is wrong. */
export async function changeMyPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.post('/auth/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/** The signed-in lender human's own lender. 404 for operators. */
export async function getMyLender(): Promise<LenderListItem> {
  const { data } = await api.get<LenderListItem>('/lenders/me');
  return data;
}

/** One human login at a lender (`users.role === 'lender_human'`). */
export interface LenderUserSummary {
  id: string;
  email: string;
  name: string | null;
  role: string;
  is_active: boolean;
  created_at: string | null;
  last_login_at: string | null;
}

/** Operator-only: the human logins at one lender. API keys are not
 *  users and never appear here. */
export async function getLenderUsers(
  lenderId: string,
): Promise<LenderUserSummary[]> {
  const { data } = await api.get<LenderUserSummary[]>(
    `/lenders/${encodeURIComponent(lenderId)}/users`,
  );
  return data;
}

/**
 * Admin-only: turn one of the lender's human logins into a Lexta
 * operator (no admin tier). The user signs in as an operator from
 * their next login; the lender keeps its entries, keys and other
 * logins. 403 for non-admins, 409 for an inactive login.
 */
export async function makeLenderUserOperator(
  lenderId: string,
  userId: string,
): Promise<LenderUserSummary> {
  const { data } = await api.post<LenderUserSummary>(
    `/lenders/${encodeURIComponent(lenderId)}/users/${encodeURIComponent(userId)}/make-operator`,
  );
  return data;
}

// ---------------------------------------------------------------------
// Operators directory (operator-only)
// ---------------------------------------------------------------------
//
// Backed by `backend/app/api/v1/endpoints/operators.py`:
//   GET    /operators                         — every operator account +
//                                               every still-pending
//                                               operator invite
//   POST   /operators/{id}/deactivate         — login refused from now on
//   POST   /operators/{id}/reactivate
//   DELETE /operators/invites/{invite_id}     — revoke a pending invite

export interface OperatorListItem {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  /** True for the signed-in operator's own row — the UI disables the
   *  deactivate action there (the backend refuses it too). */
  is_self: boolean;
  /** Admin tier — a flag on top of the operator role. */
  is_admin: boolean;
  created_at: string | null;
  last_login_at: string | null;
}

export interface PendingOperatorInvite {
  id: string;
  email: string;
  created_at: string | null;
  expires_at: string;
  invited_by_email: string | null;
}

export interface OperatorsOverview {
  operators: OperatorListItem[];
  pending_invites: PendingOperatorInvite[];
  /** Whether the signed-in caller holds the admin tier — resolved
   *  server-side at read time, so it is fresh on every page load. */
  viewer_is_admin: boolean;
}

export async function getOperators(): Promise<OperatorsOverview> {
  const { data } = await api.get<OperatorsOverview>('/operators');
  return data;
}

export async function deactivateOperator(
  operatorId: string,
): Promise<OperatorListItem> {
  const { data } = await api.post<OperatorListItem>(
    `/operators/${encodeURIComponent(operatorId)}/deactivate`,
  );
  return data;
}

export async function reactivateOperator(
  operatorId: string,
): Promise<OperatorListItem> {
  const { data } = await api.post<OperatorListItem>(
    `/operators/${encodeURIComponent(operatorId)}/reactivate`,
  );
  return data;
}

/** Admin-only: grant the admin tier to an active operator. */
export async function promoteOperator(
  operatorId: string,
): Promise<OperatorListItem> {
  const { data } = await api.post<OperatorListItem>(
    `/operators/${encodeURIComponent(operatorId)}/promote`,
  );
  return data;
}

/** Admin-only: remove the admin tier. Refused for the last active admin. */
export async function demoteOperator(
  operatorId: string,
): Promise<OperatorListItem> {
  const { data } = await api.post<OperatorListItem>(
    `/operators/${encodeURIComponent(operatorId)}/demote`,
  );
  return data;
}

export async function revokeOperatorInvite(inviteId: string): Promise<void> {
  await api.delete(`/operators/invites/${encodeURIComponent(inviteId)}`);
}

/**
 * "DKK 47 700 · EUR 1 200" — one segment per currency, DKK first, the
 * rest alphabetical. Used wherever a total spans several entries.
 */
export function formatDebtByCurrency(totals: Record<string, number>): string {
  const codes = Object.keys(totals).sort((a, b) =>
    a === 'DKK' ? -1 : b === 'DKK' ? 1 : a.localeCompare(b),
  );
  return codes.map((c) => `${c} ${formatDKK(totals[c])}`).join(' · ');
}

/** "SEK 8 000" — an amount with its own currency code, never assumed DKK. */
export function formatMoney(amount: number, currency?: string | null): string {
  return `${currency || 'DKK'} ${formatDKK(amount)}`;
}

/** Currencies Lexta prices and buys in, each in its own currency (EUR is not). */
export function isPriceableCurrency(currency: string | null | undefined): boolean {
  const code = (currency || 'DKK').toUpperCase();
  return code === 'DKK' || code === 'SEK' || code === 'NOK';
}

/** The currencies a money view can be switched between. */
export type MoneyCurrency = 'DKK' | 'SEK' | 'NOK';

export function formatDKK(amount: number): string {
  // Tabular numerals on screen come from the font-feature-settings;
  // formatting just splits with thin space. No decimal padding —
  // register amounts are always whole DKK.
  return Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// =====================================================================
// Debtor self-service
// =====================================================================
//
// A debtor authenticates with email + password (same /auth/login flow
// as operators / lender_humans). Their JWT carries `kind: "debtor"`
// plus the SHA-256 hash of their CPR as `subject_key`. The backend
// authorises `GET /debtor/cases` from that claim alone — the SPA does
// not send the CPR or subject_key in the request body.
//
// Debtors are deliberately scoped to read-only on their own cases.
// They can NOT call `/lookup`, `/register/entries`, or `/register/
// entries/open`. Server-side those endpoints reject `kind=debtor`.

/**
 * Lifecycle status of a single register entry, mirrored from the
 * backend `RegisterEntry.status` enum.
 *
 * `paid_off` is set when a Lexta-owned claim is fully settled by the
 * debtor (auto-transition when the running total of payments reaches
 * the Lexta valuation) or the operator force-closes for less than
 * 100% (settle-for-less). It is excluded from lookup so other lenders
 * don't see resolved debts.
 */
export type EntryStatus = 'active' | 'closed' | 'auto_expired' | 'paid_off';

/**
 * One register entry projected for the debtor view. Note that we do
 * NOT include the debtor's own name/CPR — they already know who they
 * are, and Lexta verifies via the JWT's `subject_key` claim. The
 * payload is otherwise a strict subset of the operator-facing entry.
 */
export interface DebtorCase {
  entry_id: string;
  status: EntryStatus;
  originated_from: OriginatedFrom;
  principal_amount: number;
  fees_amount: number | null;
  repaid_amount: number;
  outstanding_amount: number;
  currency: string;
  opened_at: string;
  last_payment_activity_at: string | null;
  closed_at: string | null;
  auto_expired_at: string | null;
  creditor_contact: CreditorContact;
  payment_destination: string | null;
  has_active_dispute: boolean;
  /** Current state of the dispute for this entry, if any. Null when no
   *  dispute exists. Terminal states (`resolved_*`, `withdrawn`) still
   *  appear here for a short while; the UI treats them as "no active
   *  dispute" for the purpose of the "Åbn indsigelse" button. */
  dispute_state?: DisputeState | null;
  /** Id of the active dispute for this entry, if any. Lets the
   *  DebtorCasesPage deep-link straight to the dispute detail without a
   *  second round-trip. */
  dispute_id?: string | null;
  /**
   * Most-recent ISO timestamp at which ownership of the entry shifted
   * to a different lender. Set the moment a debt purchase is accepted.
   * Optional because v1 of `/debtor/cases` does not yet project this
   * field — backend can add it without a migration since it lives
   * alongside `previous_owners` on the register entry. The
   * DebtorCasesPage shows the transfer banner only when this is set
   * AND `previous_owners` is non-empty (otherwise we can't say to
   * whom the case was transferred).
   *
   * TODO(api): expose `transferred_at` + `previous_owners` on the
   * `/debtor/cases` projection once Lexta Inkasso onboarding lands.
   */
  transferred_at?: string | null;
  /**
   * Names of any prior owners. Most-recent-first ordering matches the
   * way audit log entries are listed. Same TODO as `transferred_at` —
   * v1 may not populate this; the banner falls back gracefully.
   */
  previous_owners?: string[] | null;
  /**
   * State of any in-flight purchase offer on this entry. Same TODO as
   * above — when set to a non-terminal state, the DebtorCasesPage
   * shows the "case is under negotiation" banner.
   */
  purchase_offer_state?: OfferState | null;
}

export async function getDebtorCases(): Promise<DebtorCase[]> {
  const { data } = await api.get<DebtorCase[]>('/debtor/cases');
  return data;
}

// =====================================================================
// Disputes
// =====================================================================
//
// State machine:
//   open_with_originating_lender → lender_responded → operator_review
//                                                   → resolved_upheld
//                                                   → resolved_removed
//   plus `withdrawn` (terminal, debtor-initiated).
//
// All endpoints are role-gated on the backend. The SPA mirrors that
// gating so it doesn't even render actions the user isn't allowed to
// take, but it never relies on the SPA-side gate alone.

export type DisputeState =
  | 'open_with_originating_lender'
  | 'lender_responded'
  | 'operator_review'
  | 'resolved_upheld'
  | 'resolved_removed'
  | 'withdrawn';

/**
 * Terminal (closed) dispute states. A dispute in any of these states is
 * read-only — no further actions can be taken, the timeline is final.
 */
export const TERMINAL_DISPUTE_STATES: ReadonlySet<DisputeState> = new Set([
  'resolved_upheld',
  'resolved_removed',
  'withdrawn',
]);

export function isTerminalDisputeState(
  state: DisputeState | null | undefined,
): boolean {
  return state != null && TERMINAL_DISPUTE_STATES.has(state);
}

/**
 * One entry in the dispute event timeline. `action` is a backend-defined
 * opaque slug (e.g. `opened`, `lender_responded`, `escalated`,
 * `resolved`, `withdrawn`). The SPA renders these via translation keys
 * `disputes.action.{action}`; unknown slugs fall back to the raw string.
 */
export interface DisputeEvent {
  at: string;
  action: string;
  actor_user_id: string;
  actor_role: string;
  note: string | null;
  payload?: Record<string, unknown>;
  /** A Lexta administrator wrote this event while logged in as the
   *  lender (ADMIN-INDGREB). The fact only; who stays in the audit trail.
   *  Absent on responses that predate the mark. */
  admin_intervention?: boolean;
}

export interface DisputeListItem {
  id: string;
  entry_id: string;
  state: DisputeState;
  subject_name: string | null;
  originating_lender_name: string;
  reason: string;
  opened_at: string;
  last_event_at: string;
}

export interface DisputeLenderResponse {
  response_text: string;
  document_ids: string[];
  at: string;
}

export interface DisputeOperatorResolution {
  outcome: 'upheld' | 'removed';
  note: string;
  at: string;
}

export interface DisputeResponse {
  id: string;
  entry_id: string;
  state: DisputeState;
  reason: string;
  subject_name: string | null;
  originating_lender_name: string;
  events: DisputeEvent[];
  evidence_document_ids: string[];
  lender_response: DisputeLenderResponse | null;
  operator_resolution: DisputeOperatorResolution | null;
  opened_at: string;
}

export interface DisputeListResponse {
  items: DisputeListItem[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface GetDisputesParams {
  /** Filter to one or more states. Backend accepts repeated `?state=`. */
  state?: DisputeState[];
  page?: number;
  page_size?: number;
}

/**
 * Debtor-only: open a new dispute against a register entry. Backend
 * derives the debtor identity from the JWT — the SPA does not pass any
 * subject_key.
 */
export async function openDispute(
  entryId: string,
  reason: string,
  evidenceDocumentIds?: string[],
): Promise<DisputeResponse> {
  const { data } = await api.post<DisputeResponse>(
    `/entries/${entryId}/disputes`,
    {
      reason,
      evidence_document_ids: evidenceDocumentIds ?? [],
    },
  );
  return data;
}

/**
 * Lender-only: post a written response to a dispute, optionally with
 * supporting documents. Transitions state from
 * `open_with_originating_lender` → `lender_responded`.
 */
export async function respondToDispute(
  id: string,
  responseText: string,
  documentIds?: string[],
): Promise<DisputeResponse> {
  const { data } = await api.post<DisputeResponse>(`/disputes/${id}/respond`, {
    response_text: responseText,
    evidence_document_ids: documentIds ?? [],
  });
  return data;
}

/**
 * Debtor or operator: escalate a dispute to operator review. Debtors
 * use this when they're not satisfied with the lender's response.
 * Operators use it to "pick up" a dispute themselves.
 */
export async function escalateDispute(
  id: string,
  reason?: string,
): Promise<DisputeResponse> {
  const { data } = await api.post<DisputeResponse>(`/disputes/${id}/escalate`, {
    reason: reason ?? null,
  });
  return data;
}

/**
 * Operator-only: resolve a dispute. `outcome === 'upheld'` keeps the
 * register entry; `outcome === 'removed'` voids it. The note is a
 * mandatory free-text rationale, surfaced in the timeline.
 */
export async function resolveDispute(
  id: string,
  outcome: 'upheld' | 'removed',
  note: string,
): Promise<DisputeResponse> {
  const { data } = await api.post<DisputeResponse>(`/disputes/${id}/resolve`, {
    outcome,
    note,
  });
  return data;
}

/**
 * Debtor-only: withdraw a dispute. Terminal state. The entry stays
 * filed; only the dispute case is closed.
 */
export async function withdrawDispute(id: string): Promise<DisputeResponse> {
  const { data } = await api.post<DisputeResponse>(`/disputes/${id}/withdraw`);
  return data;
}

/**
 * Role-scoped list. Operators see every dispute; lenders see disputes
 * on their own entries; debtors see their own disputes. The backend
 * applies the gate from the JWT — the SPA passes only filter params.
 *
 * State filter is sent as a repeated `?state=` query parameter; axios's
 * default array serializer would render `?state[]=`, which the backend
 * doesn't recognise.
 */
export async function getDisputes(
  params: GetDisputesParams = {},
): Promise<DisputeListResponse> {
  const { data } = await api.get<DisputeListResponse>('/disputes', {
    params: {
      state: params.state,
      page: params.page,
      page_size: params.page_size,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
  return data;
}

export async function getDisputeById(id: string): Promise<DisputeResponse> {
  const { data } = await api.get<DisputeResponse>(`/disputes/${id}`);
  return data;
}

// =====================================================================
// Inventory (operator + lender_human view)
// =====================================================================
//
// The inventory endpoints back the `/entries` console: a paginated,
// filterable list of every register entry the caller is allowed to see,
// plus a per-entry detail view used by `/entries/:id`. The shape is a
// strict superset of the lookup hits — same `subject_key` /
// `originating_lender_id` semantics, but exposed with the lender's
// human-readable name and the full lifecycle metadata.
//
// `tsc --noEmit` is the contract here: the inventory backend must match
// these types exactly. If it diverges, the frontend won't compile.

/**
 * Document attached to a register entry, as the detail endpoint returns
 * it: the same `type | filename | url` triad the SPA sends at filing
 * (see `RegisterDocument`), plus the server-stamped `uploaded_at`. The
 * document's id and byte size live on the `documents` collection, not
 * on the entry — `url` is the only handle the detail view has.
 */
export interface Document {
  type: DocumentType | string;
  filename: string;
  url: string;
  uploaded_at: string;
}

/**
 * One loan within a register entry, as the "claims" addendum
 * (2026-09-16) to the creditor-attribution design has it: a register
 * entry is the creditor's aggregate on one debtor, and each loan that
 * feeds it is a claim. `principal_amount` / `repaid_amount` /
 * `debt_amount` on the entry itself are the sums over ACTIVE claims —
 * see `docs/superpowers/specs/2026-09-15-creditor-attribution-design.md`.
 * Legacy entries filed before claims existed are read as one implicit
 * active claim equal to the entry totals; the backend always sends at
 * least one claim.
 */
export interface ClaimView {
  id: string;
  reference: string | null;
  principal_amount: number;
  repaid_amount: number;
  debt_amount: number;
  loan_disbursed_at: string | null;
  default_since: string | null;
  filed_at: string;
  filed_by_lender_id: string;
  status: 'active' | 'withdrawn';
  withdrawn_at: string | null;
  withdraw_reason: string | null;
  /** A Lexta administrator added this loan while logged in as the lender
   *  (ADMIN-INDGREB). The fact only. Absent on older responses. */
  admin_intervention?: boolean;
  /** ...and/or withdrew it. The withdrawal carries its own mark, so the
   *  two steps can be by different people. */
  withdrawn_admin_intervention?: boolean;
}

/**
 * One row in the inventory listing. The list endpoint returns the bare
 * minimum a table needs — no documents, no full history — so paginated
 * scans stay cheap. Fetch the detail endpoint for the rest.
 */
export interface InventoryEntry {
  id: string;
  subject_key: string;
  subject_name: string | null;
  originating_lender_id: string;
  originating_lender_name: string;
  /** Lender row whose API key filed the entry — equal to
   *  `originating_lender_id` for direct filings. Differs when a
   *  `platform` lender filed on behalf of a creditor (see
   *  `beneficial_creditor`). Null on legacy rows written before the
   *  field existed. */
  filed_by_lender_id: string | null;
  /** Display name of `filed_by_lender_id`. List + detail only. */
  filed_by_lender_name: string | null;
  /** `platform` when a lender with `kind: "platform"` filed this entry
   *  via `on_behalf_of`; `direct` for an ordinary self-filed entry;
   *  `null` on legacy rows. */
  filing_channel: 'direct' | 'platform' | null;
  /** Who the filing platform says actually holds the claim. Present
   *  only for `filing_channel === 'platform'` entries. */
  beneficial_creditor: BeneficialCreditorView | null;
  principal_amount: number;
  fees_amount: number | null;
  repaid_amount: number;
  debt_amount: number;
  currency: string;
  status: EntryStatus;
  originated_from: OriginatedFrom;
  opened_at: string;
  /** Loan disbursement date — ISO date string (YYYY-MM-DD). */
  loan_disbursed_at: string | null;
  /** Date the borrower first missed an expected payment — ISO date string. */
  default_since: string | null;
  last_payment_activity_at: string | null;
  closed_at: string | null;
  auto_expired_at: string | null;
  dispute_state: string | null;
  /** Lexta-side fields. Populated only while Lexta currently owns
   *  the claim; cleared on sell-back. The list view surfaces these
   *  as "Y of X paid" inline. */
  lexta_valuation: number | null;
  lexta_amount_paid: number | null;
  lexta_residual: number | null;
  /** `settled` (debt resolved) vs `withdrawn` (filed in error) for
   *  closed rows. Null on rows closed before the field existed. */
  close_kind: 'settled' | 'withdrawn' | null;
  /** ADMIN-INDGREB flags: a Lexta administrator logged in as the lender
   *  ("Log ind som") did it. Each is the fact only; who stays in the audit
   *  trail. All are false or absent on every other entry, and the debtor's
   *  case list never carries them. */
  /** The filing. */
  opened_admin_intervention?: boolean;
  /** The LAST amend of principal, repaid, debt or fees. A real user's
   *  later amend of them clears it. */
  amounts_admin_intervention?: boolean;
  /** The last change of `payment_destination`, cleared the same way. */
  payment_instruction_admin_intervention?: boolean;
  /** The standing consent was last withdrawn by an administrator. The
   *  next grant clears it; while a consent stands it carries its own
   *  mark (`AutoSaleConsent.admin_intervention`). */
  consent_withdrawn_admin_intervention?: boolean;
  /** Closed or withdrawn by an administrator, or paid off by a payment
   *  one booked on a Lexta-owned entry. */
  close_admin_intervention?: boolean;
  /** Operator who ran the purchase that moved the entry to its current
   *  owner. Shown under the owner chip as "overtaget af …". */
  transferred_by_name: string | null;
  /** Live purchase offer on this entry, if any. */
  open_offer_state: OfferState | null;
  /** True when the caller has to make the next move on that offer. */
  offer_awaiting_you: boolean;
  /** Number of active claims (loans) making up this entry's totals.
   *  1 on every legacy and single-loan entry; the entries list uses
   *  this to show a "N krav" tag without fetching the detail view. */
  claims_active_count: number;
  /** How many OTHER active entries the register holds on this row's
   *  subject — what a lookup on the subject would return besides this
   *  row. 0 on most rows; the list shows a "+N poster" tag otherwise. */
  subject_other_active_count: number;
  /** Which operator is working this entry. Operator-only: the API
   *  returns it only to operator principals; lenders always get null.
   *  Missing on responses from write endpoints that answer with the
   *  bare register shape, so read it defensively. */
  collection_handler?: CollectionHandler | null;
  /** The live fixed-price quote — see `SaleQuote`. Present on list and
   *  detail reads for everyone who may read the entry; absent on write
   *  responses, so read it defensively. */
  sale_quote?: SaleQuote | null;
  /** Standing consent to a fixed-price sale to Lexta, or null. */
  auto_sale_consent?: AutoSaleConsent | null;
  /** "Forsøgt og henlagt": Lexta bought, collected and gave the entry
   *  back. Both parties see it. */
  collection_attempt?: CollectionAttempt | null;
  /** "Påbegyndt hos Lexta": when Lexta started collecting. Visible to
   *  the owning lender; the operator's name stays operator-only. */
  lexta_collection_started_at?: string | null;
  /** "Under behandling hos Lexta": an operator has taken this consented
   *  entry under treatment. Lender-visible, no name; the lender can
   *  neither close it nor withdraw the consent meanwhile. */
  under_lexta_treatment_since?: string | null;
  /** When the treatment runs out unless Lexta buys or makes an
   *  agreement (the marker's 30-day frist). Null while an agreement
   *  holds the entry. */
  under_lexta_treatment_until?: string | null;
  collection_agreed?: CollectionAgreed | null;
  /** The inddrivelsesaftale — the active one (list rows), else the
   *  newest that ended (detail). Operator and owning lender only. */
  collection_agreement?: CollectionAgreement | null;
  /** The status-overview slice of the newest via-lender-account case on
   *  this entry (live or closed), or null. List rows only. */
  collection?: CollectionOverview | null;
}

/**
 * The status-overview columns of the operator's list: one via-lender-
 * account case per row, every figure from the same ledger the offer
 * page shows.
 */
export interface CollectionOverview {
  offer_id: string;
  settlement_mode: SettlementMode | null;
  auto_sale: boolean;
  status: CollectionStatus;
  /** The price Lexta pays the lender. */
  agreed_amount: number;
  /** Slutbeløb, once agreed with the debtor. */
  agreed_with_debtor_amount: number | null;
  received_total: number;
  lender_share: number;
  /** Långivers saldo — what the lender is still owed of the price. */
  lender_outstanding: number;
  /** Lextas saldo — what the lender holds for Lexta. */
  lexta_outstanding: number;
  remaining: number | null;
  started_at: string | null;
  days_since_started: number | null;
  agreement_deadline_at: string | null;
  deadline_expired: boolean;
  closed_at: string | null;
}

export interface CollectionAttempt {
  shelved_at: string | null;
  note: string | null;
  /** What the debtor paid while Lexta collected — folded into the entry. */
  received_total: number;
}

/**
 * "Må sælges til Lexta for <pct> % af tabssaldoen" — a standing
 * permission on the entry, not a sale. Lexta may buy at the platform's
 * sale pct and need not; it covers both settlement forms (Lexta decides
 * whether the lender is paid a lump sum or keeps collecting on Lexta's
 * behalf). `sale_pct` is the percentage the lender saw when consenting.
 */
export interface AutoSaleConsent {
  sale_pct: number;
  at: string | null;
  /** True when the consent carries the locked amounts below — every
   *  consent given since 2026-09-22 does. The lender's consent froze
   *  hovedstol, gebyrer, indbetalt, restgæld, tabssaldo and the price. */
  locked: boolean;
  principal_amount: number | null;
  fees_amount: number | null;
  repaid_amount: number | null;
  debt_amount: number | null;
  tabssaldo: number | null;
  price: number | null;
  /** The consent was given by a Lexta administrator logged in as the
   *  lender (ADMIN-INDGREB). The fact only. Absent on older responses. */
  admin_intervention?: boolean;
}

/** What a lender consents to: `GET /register/sale-terms`. */
export interface SaleTerms {
  sale_pct: number;
  auto_sale_enabled: boolean;
  /** The calling lender's default for the filing-form checkbox; false for operators. */
  default_consent: boolean;
  /** The default now standing was set by a Lexta administrator logged in
   *  as the lender (ADMIN-INDGREB next to the checkbox). The default is
   *  advisory and not itself a consent. The lender's own next change
   *  clears it. */
  default_consent_admin_intervention?: boolean;
}

export async function getSaleTerms(): Promise<SaleTerms> {
  const { data } = await api.get<SaleTerms>('/register/sale-terms');
  return data;
}

/** Result of `POST /register/entries/consent-all`. */
export interface ConsentAllResult {
  consented: number;
  skipped_already: number;
  skipped_not_priceable: number;
  entry_ids: string[];
}

/** Give consent to a fixed-price sale on every active entry of the calling lender. Lender only. */
export async function consentAllEntries(): Promise<ConsentAllResult> {
  const { data } = await api.post<ConsentAllResult>('/register/entries/consent-all', {});
  return data;
}

/** Set the calling lender's default for the filing-form consent checkbox. Lender only. */
export async function setDefaultConsent(value: boolean): Promise<SaleTerms> {
  const { data } = await api.put<SaleTerms>('/register/sale-terms/default', { default_consent: value });
  return data;
}

/**
 * The live fixed-price quote on an entry (auto-sale design, 2026-09-21).
 * `tabssaldo` = principal − repaid (the lender's capital loss; fees and
 * interest are not for sale). `price` = `sale_pct` % of it. When there is
 * nothing to price the backend says why instead of quoting zero:
 *   no_principal   — legacy debt-only row, no principal on file
 *   zero_tabssaldo — everything lent has come back
 *   currency       — only DKK, SEK and NOK are priced (each in its own
 *                    currency, no conversion); EUR is not
 */
export interface SaleQuote {
  tabssaldo: number | null;
  priceable: boolean;
  reason: 'no_principal' | 'zero_tabssaldo' | 'currency' | null;
  currency: string;
  sale_pct: number;
  price: number | null;
  /** The fee is this pct of Lexta's profit, booked when the case
   *  closes — no amount up front (one fee rule, 2026-09-28). */
  fee_pct: number;
  /** Locked by the lender's consent: tabssaldo, pct and price are the
   *  consent's; what the lender received since is deducted. */
  locked: boolean;
  locked_at: string | null;
  received_since_consent: number;
  /** What Lexta pays today: the locked price minus payments received. */
  price_remaining: number | null;
  /** Received beyond the locked price — "tilbagebetales til Lexta". */
  lexta_share: number;
  /** The collection amount: what an operator agreed with the debtor
   *  (`agreed`) or the restgæld the consent froze (`consent`). */
  collection_basis: number | null;
  collection_basis_kind: 'agreed' | 'consent' | null;
  collection_agreed_at: string | null;
  collection_remaining: number | null;
}

/** The inddrivelsesbeløb an operator agreed with the debtor on a
 *  consented entry. */
export interface CollectionAgreed {
  amount: number;
  at: string | null;
  note: string | null;
}

/**
 * "I am collecting on this one." An operator marks an active entry so
 * a colleague does not start on it too. Lexta-internal — never shown to
 * lenders or debtors, not a partner event. Written through the
 * `/operator/register/entries/{id}/collection/…` routes below.
 */
export interface CollectionHandler {
  operator_user_id: string | null;
  /** Operator name, e-mail as the fallback. */
  operator_label: string | null;
  started_at: string | null;
  note: string | null;
  /** The frist: 30 days from the start unless extended. Null when the
   *  case has no frist on the marker (Lexta bought it, or an agreement
   *  holds it). */
  deadline_at: string | null;
  /** The frist ran out — the marker binds nothing any more. */
  expired: boolean;
}

// =====================================================================
// Inddrivelsesaftale — "Vi laver aftale" (2026-09-28)
// =====================================================================
//
// Lexta agrees an installment plan with the debtor on a consented,
// lender-owned entry instead of buying it. The debtor pays the lender,
// who records each payment as usual; of everything received since the
// consent the price comes first to the lender and the rest is Lexta's.
// Written by operators; read by the operator and the owning lender
// (without the fee and operator names).

export type AgreementState = 'active' | 'closed' | 'shelved';
export type AgreementStatus =
  | 'not_started'
  | 'on_track'
  | 'behind'
  | 'fulfilled'
  | 'closed'
  | 'shelved';

export interface AgreementTerms {
  total_amount: number | null;
  installment_amount: number | null;
  first_due_date: string | null;
  note: string | null;
  lender_share?: number | null;
  lender_share_note?: string | null;
}

export interface AgreementTermsChange {
  at: string | null;
  by_user_id: string | null;
  by_label: string | null;
  note: string | null;
  previous: AgreementTerms | null;
  new: AgreementTerms | null;
}

export interface CollectionAgreement {
  id: string;
  entry_id: string;
  originating_lender_id: string;
  originating_lender_name: string | null;
  entry_subject_name: string | null;
  currency: string;
  state: AgreementState;
  total_amount: number;
  installment_amount: number;
  first_due_date: string | null;
  note: string | null;
  installment_count: number;
  last_due_date: string | null;
  status: AgreementStatus;
  due_to_date: number;
  received_total: number;
  remaining: number;
  arrears: number;
  next_due_date: string | null;
  next_due_amount: number | null;
  last_payment_at: string | null;
  /** The lender's share: the locked price, or a særaftale's lower figure. */
  price: number;
  /** The locked price (80 % of tabssaldo). */
  price_original: number;
  /** A særaftale with the lender lowered its share. */
  special_agreement: boolean;
  lender_share_note: string | null;
  received_since_consent: number;
  lender_share: number;
  lexta_profit: number;
  created_at: string | null;
  created_by_user_id: string | null;
  created_by_label: string | null;
  updated_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
  shelve_note: string | null;
  terms_history: AgreementTermsChange[];
  /** Operator only. */
  platform_fee_pct: number | null;
  platform_fee_basis: number | null;
  platform_fee_amount: number | null;
  platform_fee_booked_at: string | null;
}

export interface AgreementTermsInput {
  total_amount: number;
  installment_amount: number;
  /** `YYYY-MM-DD`. */
  first_due_date: string;
  note?: string | null;
  /** Særaftale: a lender share below the locked price, with its note.
   *  The total must always cover the lender's share. */
  lender_share?: number | null;
  lender_share_note?: string | null;
  /** Terms change only: drop the særaftale. */
  clear_lender_share?: boolean;
}

export async function createCollectionAgreement(
  entryId: string,
  terms: AgreementTermsInput,
): Promise<CollectionAgreement> {
  const { data } = await api.post<CollectionAgreement>(
    `/register/entries/${encodeURIComponent(entryId)}/collection-agreement`,
    { ...terms, note: terms.note ?? null },
  );
  return data;
}

export async function updateAgreementTerms(
  agreementId: string,
  changes: Partial<AgreementTermsInput> & { reason: string },
): Promise<CollectionAgreement> {
  const { data } = await api.post<CollectionAgreement>(
    `/collection-agreements/${encodeURIComponent(agreementId)}/terms`,
    changes,
  );
  return data;
}

export async function shelveAgreement(agreementId: string, note: string): Promise<CollectionAgreement> {
  const { data } = await api.post<CollectionAgreement>(
    `/collection-agreements/${encodeURIComponent(agreementId)}/shelve`,
    { note },
  );
  return data;
}

export async function listCollectionAgreements(
  params: { state?: AgreementState; all_states?: boolean } = {},
): Promise<CollectionAgreement[]> {
  const { data } = await api.get<{ items: CollectionAgreement[] }>('/collection-agreements', {
    params,
  });
  return data.items;
}

/**
 * Another active register entry on the same subject as the one being
 * read — the same projection a lookup hit gets: creditor by display
 * name, amount, dates, flags. `readable` says whether the caller may
 * open it via `getEntryDetail` (operator, owner, or filing platform);
 * a peer lender's entry is shown as a fact, not offered as a link.
 */
export interface SubjectEntryRef {
  entry_id: string;
  originating_lender_name: string;
  debt_amount: number;
  currency: string;
  opened_at: string;
  last_payment_activity_at: string | null;
  has_active_dispute: boolean;
  is_incomplete: boolean | null;
  readable: boolean;
}

/**
 * Detail view extends the list row with the heavy fields: address,
 * documents, creditor contact, payment destination, history.
 */
/** One note of an entry's case history (see `EntryDetail.history`). */
export interface HistoryNote {
  /** Stable id of the block — pass to `removeHistoryNote`. */
  id: string;
  at: string | null;
  actor_kind: 'operator' | 'lender' | null;
  actor_id: string | null;
  /** Operator name / e-mail or lender display name, resolved server-side. */
  actor_label: string | null;
  text: string;
  /** False for the record itself (filing text, automatic claim lines):
   *  no Fjern button, and the API answers 409 if tried. */
  removable: boolean;
}

export interface EntryDetail extends InventoryEntry {
  /** The subject's other active entries, newest first. Empty on the
   *  common single-creditor case. */
  other_entries: SubjectEntryRef[];
  subject_address: string | null;
  /** Optional debtor contact fields — projected by the detail
   *  endpoint so the entry-detail screen can render whatever
   *  channels were captured at filing time. */
  subject_phone: string | null;
  subject_email: string | null;
  subject_postal_code: string | null;
  subject_city: string | null;
  subject_facebook_url: string | null;
  /** Soft identifier (ISO date). Derived from the CPR when filed with one. */
  subject_birth_date?: string | null;
  /** Country of the identity number the entry is keyed on; DK for
   *  CPR-keyed rows, null while the entry is CPR-less. */
  id_country?: string | null;
  history_notes: string | null;
  /** `history_notes` parsed into one block per note, author resolved by
   *  name. Present on `GET /entries/{id}`; absent on write responses. */
  history?: HistoryNote[];
  documents: Document[];
  creditor_contact: CreditorContact;
  payment_destination: string | null;
  /** Free text from the lender about the subject ("Oplysninger fra
   *  långiver"). Operator and owning lender read and write it. */
  notes: string | null;
  /** Lexta's own notepad. Present only for operator principals. */
  operator_notes?: string | null;
  /** Most-recent transfer timestamp; populated whenever an offer
   *  accept fired the ownership-flip on this entry. */
  transferred_at: string | null;
  last_transfer_amount: number | null;
  /** Append-only chain of prior ownerships. Each row captures the
   *  state at the moment of one transfer (in or out of Lexta). */
  previous_owners: PreviousOwner[];
  /** Set when status flips to `paid_off` — auto-transition or
   *  operator force-close (settle-for-less). */
  paid_off_at: string | null;
  close_reason: string | null;
  /** Non-null while a purchase offer is accepted but the seller has not
   *  yet confirmed payment. Ownership has not moved. */
  pending_transfer_offer_id: string | null;
  /** True when the entry was filed without CPR (mobile/FB/name+address
   *  alone). Cleared once `attach-cpr` succeeds; reinstated when an
   *  operator calls `revert-cpr-attachment`. Null on legacy entries
   *  written before this field existed. */
  is_incomplete: boolean | null;
  /** The loans making up this entry's totals — see `ClaimView`. Always
   *  at least one row, even on legacy entries (the backend materialises
   *  the implicit claim on read). */
  claims: ClaimView[];
  /** Lender-side payment ledger, oldest first, struck lines included.
   *  Only lender-owned entries write to it. */
  payments: PaymentView[];
}

/**
 * Per-currency sums over EVERY entry the list's filters matched — all
 * pages, not just the one returned. One row per currency, sorted by
 * code: the register never converts, so DKK and NOK are never added
 * together. The status-overview figures (`tabssaldo` onwards) are
 * populated for operators only and `null` for lenders, who never see
 * those columns; a figure with nothing to add up is `null`, never 0.
 */
export interface InventoryCurrencyTotals {
  currency: string;
  count: number;
  /** Hovedstol. */
  principal_amount: number;
  /** Indbetalt on the entries themselves. */
  repaid_amount: number;
  /** The outstanding claim ("Resterende skyldigt"). */
  debt_amount: number;
  /** Tabssaldo — the quotes' basis. */
  tabssaldo: number | null;
  /** What Lexta pays or paid: the payable quote on priceable rows, else
   *  the case's agreed price where a row has a case. */
  sale_price: number | null;
  /** Slutbeløb agreed with the debtors, over rows with a case. */
  agreed_with_debtor_amount: number | null;
  /** Indbetalt as the status overview shows it: the case's received
   *  total where a row has a case, else the entry's own repaid. */
  received_amount: number | null;
  /** Långivers saldo, over rows with a case. */
  lender_outstanding: number | null;
  /** Lextas saldo, over rows with a case. */
  lexta_outstanding: number | null;
}

/**
 * Page envelope for the inventory list.
 */
export interface InventoryListResponse {
  items: InventoryEntry[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  /** Per-currency totals over the whole filtered set. Empty when
   *  nothing matched. */
  totals: InventoryCurrencyTotals[];
}

/**
 * Filters / pagination params for `getEntries`. All optional —
 * `status` is comma-joined on the wire to match the backend contract
 * (?status=active,closed). Date params expect ISO strings (YYYY-MM-DD
 * is accepted; the backend coerces).
 */
export interface ListParams {
  status?: EntryStatus[];
  lender_id?: string;
  from_date?: string;
  to_date?: string;
  /** true: only rows with a standing auto-sale consent ("Kan købes"). */
  consented?: boolean;
  /** Operator only: entries the caller holds ("Jeg tager denne"). */
  mine?: boolean;
  /** Server-side search: entry id / LX-reference, subject, lender name. */
  q?: string;
  page?: number;
  page_size?: number;
}

export async function getEntries(
  params: ListParams = {},
): Promise<InventoryListResponse> {
  const query: Record<string, string | number> = {};
  if (params.status && params.status.length > 0) {
    query.status = params.status.join(',');
  }
  if (params.lender_id) query.lender_id = params.lender_id;
  if (params.from_date) query.from_date = params.from_date;
  if (params.to_date) query.to_date = params.to_date;
  if (params.consented !== undefined) query.consented = String(params.consented);
  if (params.mine) query.mine = 'true';
  if (params.q) query.q = params.q;
  if (params.page) query.page = params.page;
  if (params.page_size) query.page_size = params.page_size;
  const { data } = await api.get<InventoryListResponse>('/entries', {
    params: query,
  });
  return data;
}

export async function getEntryDetail(id: string): Promise<EntryDetail> {
  const { data } = await api.get<EntryDetail>(`/entries/${id}`);
  return data;
}

export async function closeEntry(
  id: string,
  reason: string,
  actingAsLenderId?: string,
): Promise<EntryDetail> {
  // Operators act on behalf of the entry's lender; the backend requires
  // acting_as_lender_id for operator principals and ignores it for
  // lender principals.
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/close`,
    actingAsLenderId
      ? { reason, acting_as_lender_id: actingAsLenderId }
      : { reason },
  );
  return data;
}

/**
 * Operator: record the inddrivelsesbeløb agreed with the debtor on a
 * consented entry. `POST /register/entries/{id}/collection-amount`.
 */
export async function setCollectionAmount(
  id: string,
  amount: number,
  note?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/collection-amount`,
    { amount, note: note ?? null },
  );
  return data;
}

/**
 * Originating lender pulls a filing made in error (duplicate row, wrong
 * person). Soft delete: the entry goes to `closed` with
 * `close_kind='withdrawn'`, drops off lookup at once, and stays in the
 * register for audit. Reason is mandatory.
 */
export async function withdrawEntry(
  id: string,
  reason: string,
  actingAsLenderId?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/withdraw`,
    actingAsLenderId
      ? { reason, acting_as_lender_id: actingAsLenderId }
      : { reason },
  );
  return data;
}

/** Body of `addEntryClaim` — same validators as the open-entry request:
 *  `debt_amount = principal_amount + fees − repaid_amount`, `debt_amount
 *  ≥ 0`. `reference` is the platform's own loan number, if any. */
export interface AddClaimPayload {
  reference?: string;
  principal_amount: number;
  repaid_amount: number;
  debt_amount: number;
  loan_disbursed_at?: string;
  default_since?: string;
  history_note?: string;
}

/**
 * Add a loan to an entry that already carries a claim on this debtor
 * (the collision the "claims" addendum exists for — see
 * `docs/superpowers/specs/2026-09-15-creditor-attribution-design.md`).
 * Same authz as `withdrawEntry`: the owner, or the filing platform
 * under an active `manage` link.
 */
export async function addEntryClaim(
  id: string,
  payload: AddClaimPayload,
  actingAsLenderId?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/claims`,
    actingAsLenderId
      ? { ...payload, acting_as_lender_id: actingAsLenderId }
      : payload,
  );
  return data;
}

/**
 * Withdraw one claim (loan) from an entry — the filing was a mistake
 * for that loan specifically, distinct from withdrawing the whole
 * entry. When it empties the entry of active claims the backend
 * withdraws the entry too, exactly as `withdrawEntry` would.
 */
export async function withdrawEntryClaim(
  id: string,
  claimId: string,
  reason: string,
  actingAsLenderId?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/claims/${claimId}/withdraw`,
    actingAsLenderId
      ? { reason, acting_as_lender_id: actingAsLenderId }
      : { reason },
  );
  return data;
}

/** Content types the browser may render inline; everything else is
 *  handed over as a download under the original filename so no
 *  attacker-typed HTML/SVG ever renders in the app origin. */
export const INLINE_SAFE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

/**
 * Authenticated document fetch. Plain `<a href>` navigation never
 * carries the Bearer header, so the download endpoint 404s — fetch the
 * bytes through the axios instance instead and hand back a Blob.
 */
export async function fetchDocumentBlob(url: string): Promise<Blob> {
  // `url` is the stable `/api/v1/documents/{id}` the backend minted;
  // strip the prefix so it composes with the instance baseURL.
  const path = url.replace(/^\/api\/v1/, '');
  const { data } = await api.get<Blob>(path, { responseType: 'blob' });
  return data;
}

/**
 * One line of a lender-owned entry's payment ledger. Struck lines keep
 * their row with `removed_at` set and no longer count towards the
 * totals. `recorded_by_label` is resolved on the detail read only.
 */
export interface PaymentView {
  id: string;
  amount: number;
  at: string;
  note: string | null;
  recorded_by_kind: string | null;
  recorded_by_user_id: string | null;
  recorded_by_lender_id: string | null;
  recorded_by_label: string | null;
  created_at: string | null;
  removed_at: string | null;
  removed_reason: string | null;
  /** A Lexta administrator recorded the line while logged in as the
   *  lender (ADMIN-INDGREB). The fact only. Absent on older responses. */
  admin_intervention?: boolean;
  /** ...and/or struck it. The strike carries its own mark. */
  removed_admin_intervention?: boolean;
}

export interface RecordPaymentPayload {
  /** Any amount above zero — repayments are not whole units. */
  amount: number;
  /** ISO datetime; defaults to now server-side, never in the future. */
  paid_at?: string | null;
  note?: string | null;
}

/**
 * Record a payment on an entry. On a lender-owned entry the amount
 * reduces the outstanding amount, raises `repaid_amount` and lands on
 * the payment ledger; on a Lexta-owned entry it grows the Lexta-side
 * total. Operators must say which lender they act as — the backend
 * answers 403 `acting_lender_required` otherwise.
 */
export async function recordPaymentActivity(
  id: string,
  payload: RecordPaymentPayload,
  actingAsLenderId?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/payment-activity`,
    {
      amount: payload.amount,
      paid_at: payload.paid_at ?? null,
      note: payload.note ?? null,
      ...(actingAsLenderId ? { acting_as_lender_id: actingAsLenderId } : {}),
    },
  );
  return data;
}

/**
 * Strike one payment line (typo, duplicate). The row stays, marked
 * removed, and its amount goes back onto the entry. Reason required.
 */
export async function strikePayment(
  id: string,
  paymentId: string,
  reason: string,
  actingAsLenderId?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${id}/payments/${encodeURIComponent(paymentId)}/strike`,
    actingAsLenderId ? { reason, acting_as_lender_id: actingAsLenderId } : { reason },
  );
  return data;
}

// Lenders directory: see `getLenders` / `LenderListItem` defined above
// (next to `OpenEntryPayload`). Reused by the inventory filter on
// `/entries`.

// =====================================================================
// Dashboard stats
// =====================================================================

export type StatCardKey =
  | 'total_entries'
  | 'active'
  | 'total_debt_dkk'
  | 'open_disputes';

export interface StatCardLive {
  key: StatCardKey;
  value: number;
  previous_value: number;
  delta_pct: number | null;
  delta_absolute: number | null;
  sparkline: number[];
  trend: 'up' | 'down' | 'flat';
}

export interface DashboardStatsResponse {
  window_days: number;
  cards: StatCardLive[];
  generated_at: string;
}

/** Fetch the live stat-card data for the EntriesPage dashboard row.
 *  Operator → unscoped; lender principals → server-side scoped to
 *  their own lender_id; debtor → 403. */
export async function getDashboardStats(
  windowDays = 30,
): Promise<DashboardStatsResponse> {
  const { data } = await api.get<DashboardStatsResponse>(
    '/dashboard/stats',
    { params: { window_days: windowDays } },
  );
  return data;
}

// =====================================================================
// Public landing-page stats (no auth)
// =====================================================================

/**
 * Aggregate platform stats backing the homepage stats band. The
 * endpoint is unauthenticated by design — the homepage is shown to
 * anonymous visitors who haven't signed in. Only counts and a sum
 * cross the wire; no per-record data is exposed. See
 * ``backend/app/api/v1/endpoints/public.py`` for the rationale.
 */
export interface LandingStats {
  connected_lenders: number;
  active_entries: number;
  total_debt: number;
  lookups_last_7_days: number;
}

/** Fetch the public landing-page stats. Goes through the shared axios
 *  client; the request interceptor only adds an Authorization header
 *  when a token is present, so this call is anonymous when no one is
 *  logged in. */
export async function getLandingStats(): Promise<LandingStats> {
  const { data } = await api.get<LandingStats>('/public/landing-stats');
  return data;
}

// =====================================================================
// Debt-purchase offers
// =====================================================================
//
// Lexta Inkasso (the operator) negotiates with the originating lender
// to buy a debt outright. On accept, ownership of the register entry
// flips to Lexta Inkasso and the debtor is notified in-app.
//
// State machine:
//   pending_lender_response  ⇄  pending_offerer_response
//                                       ↓
//                            accepted | rejected | withdrawn  (terminal)
//
// RBAC:
//   - operator    → sees / creates / acts on every offer
//   - lender_human → offers on entries they own; act when their turn
//   - debtor      → read-only view of their own entry's offer (no actions)
//   - lender_machine → 403 (machines don't trade)

export type OfferState =
  | 'pending_lender_response'
  | 'pending_offerer_response'
  /** Price agreed; the seller has not yet confirmed the money arrived.
   *  Ownership has NOT moved. */
  | 'accepted_pending_settlement'
  /** Legacy terminal state — accepted before settlement gating existed. */
  | 'accepted'
  /** Seller confirmed receipt; ownership transferred. Terminal. */
  | 'settled'
  /** Via-lender-account deal: Lexta owns the claim, the seller keeps
   *  receiving the debtor's installments and records them here. Ends
   *  as `settled` when the entry leaves the register. */
  | 'collecting'
  | 'rejected'
  | 'withdrawn';

/** Who is owed the purchase price and therefore confirms receipt. */
export type SettlementPayee = 'lender' | 'lexta';

/**
 * How the agreed price reaches the seller on a Lexta-buys deal.
 * `lump_sum` — Lexta pays once, seller confirms, ownership moves.
 * `via_lender_account` — ownership moves at accept; the debtor keeps
 * paying the seller, who records each installment. The first
 * `agreed_amount` is the seller's, the rest is Lexta's.
 */
export type SettlementMode = 'lump_sum' | 'via_lender_account';

/** A ledger line: `received` from the debtor, `forwarded` to Lexta. */
export type InstallmentKind = 'received' | 'forwarded';

export interface OfferInstallment {
  id: string;
  kind: InstallmentKind;
  at: string;
  amount: number;
  note: string | null;
  recorded_by_user_id: string | null;
  recorded_by_role: string | null;
  on_behalf: boolean;
  created_at: string;
  /** Set when the line was struck; struck lines leave the totals. */
  removed_at: string | null;
  removed_reason: string | null;
  /** A Lexta administrator recorded the line while logged in as the
   *  seller (ADMIN-INDGREB). The fact only. Absent on older responses. */
  admin_intervention?: boolean;
  /** ...and/or struck it. The strike carries its own mark. */
  removed_admin_intervention?: boolean;
}

/** Where a via-lender-account case stands: påbegyndt / aftale /
 *  afsluttet / henlagt. */
export type CollectionStatus = 'started' | 'agreed' | 'closed' | 'shelved';

/** Running totals of a via-lender-account deal, visible to both sides. */
export interface CollectionLedger {
  agreed_amount: number;
  received_total: number;
  lender_share: number;
  lexta_share: number;
  forwarded_total: number;
  /** `lexta_share − forwarded_total`: what the seller still holds for Lexta. */
  lexta_outstanding: number;
  /** What the lender is still owed of the price — the lender is paid
   *  first, always; Lexta never forgives this shortfall. */
  lender_outstanding: number;
  /** Slutbeløb — what Lexta agreed with the debtor — and what is left of
   *  it after the received installments. Null until agreed. */
  agreed_with_debtor_amount: number | null;
  agreed_with_debtor_at: string | null;
  remaining: number | null;
  /** Frist: a slutbeløb is due by `agreement_deadline_at`.
   *  `deadline_expired` is derived; nothing changes state by itself. */
  started_at: string | null;
  agreement_deadline_at: string | null;
  deadline_expired: boolean;
  days_since_started: number | null;
  status: CollectionStatus;
  closed_at: string | null;
  closed_reason: string | null;
  shelve_note: string | null;
  installments: OfferInstallment[];
}

/**
 * Operator only: "Forlæng frist" — move the agreement deadline on a
 * collecting case. Future date, note required.
 */
export async function extendDeadline(
  id: string,
  deadlineAt: string,
  note: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/deadline`,
    { deadline_at: deadlineAt, note },
  );
  return data;
}

/**
 * Operator only: "Tilbagefør til långiver" — give a collecting
 * via-lender-account case back. Ownership reverts, the entry is marked
 * "Forsøgt og henlagt" and can be bought again; the fee is never
 * booked. Note required. Never offered on a lump-sum deal.
 */
export async function shelveCollection(
  id: string,
  note: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/shelve`,
    { note },
  );
  return data;
}

/**
 * Operator only: the slutbeløb Lexta agreed with the debtor on a
 * collecting case. Editable; every change is audited and lands on the
 * timeline, and it becomes the entry's Lexta valuation.
 */
export async function setAgreedAmount(
  id: string,
  amount: number,
  note?: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/agreed-amount`,
    { amount, note: note ?? null },
  );
  return data;
}

/**
 * Direction of a purchase offer. `lexta_buys` (default) — operator
 * opens an offer to a creditor to acquire their claim. `lexta_sells`
 * — operator opens an offer to a chosen lender to sell back a
 * Lexta-owned claim. The state machine is symmetric; the responder
 * role flips with direction.
 */
export type OfferDirection = 'lexta_buys' | 'lexta_sells';

/**
 * Terminal (closed) offer states. An offer in any of these states is
 * read-only — no further actions can be taken.
 */
export const TERMINAL_OFFER_STATES: ReadonlySet<OfferState> = new Set([
  'accepted',
  'settled',
  'rejected',
  'withdrawn',
]);

/** States in which the entry is spoken for (no second offer allowed). */
export const OPEN_OFFER_STATES: ReadonlyArray<OfferState> = [
  'pending_lender_response',
  'pending_offerer_response',
  'accepted_pending_settlement',
  'collecting',
];

export function isTerminalOfferState(
  state: OfferState | null | undefined,
): boolean {
  return state != null && TERMINAL_OFFER_STATES.has(state);
}

/**
 * One row in the offer event timeline. `action` is a backend slug
 * (e.g. `opened`, `countered`, `accepted`, `rejected`, `withdrawn`),
 * surfaced in the UI via translation keys `purchase.action.{action}`.
 */
export interface OfferEvent {
  at: string;
  action: string;
  actor_user_id: string;
  actor_role: string;
  amount: number | null;
  note: string | null;
  /** Set on `settlement_confirmed` events an operator filed for the lender. */
  on_behalf?: boolean | null;
  /** Set on the accept event of a via-lender-account deal. */
  settlement_mode?: SettlementMode | null;
  /** Set on `deadline_extended` events: the new frist. */
  deadline_at?: string | null;
  /** Set on the events of a fixed-price buy. */
  auto_sale?: boolean | null;
  /** A Lexta administrator took this step while logged in as the lender
   *  (ADMIN-INDGREB). The fact only. Absent on older responses. */
  admin_intervention?: boolean;
}

/**
 * Full purchase-offer document, returned by all create/act endpoints
 * and the `GET /purchase-offers/{id}` detail call.
 */
export interface PurchaseOfferResponse {
  id: string;
  entry_id: string;
  entry_subject_name: string | null;
  offered_by_user_id: string;
  offered_by_buyer_lender_id: string;
  originating_lender_id: string;
  originating_lender_name: string | null;
  buyer_lender_name: string | null;
  direction: OfferDirection;
  /** Currency of every amount on the offer (the entry's own). */
  currency: string;
  current_amount: number;
  /**
   * Operator-set DKK amount Lexta plans to collect from the debtor
   * after takeover. `null` for principals on the creditor side of a
   * buy offer, or any non-buyer lender on a sell-back. Always set
   * for operators + Lexta-side / sell-back-buyer lenders.
   */
  lexta_valuation: number | null;
  state: OfferState;
  /** How the price is settled; null until a price is agreed. */
  settlement_mode: SettlementMode | null;
  /** The installment ledger of a via-lender-account deal; null otherwise. */
  collection: CollectionLedger | null;
  /** Created by the one-click buy on a consented entry: the price basis
   *  is locked in `price_snapshot` and never moves. */
  auto_sale: boolean;
  price_snapshot: PriceSnapshot | null;
  /** Who has to confirm receipt of the price before ownership moves. */
  settlement_payee: SettlementPayee | null;
  accepted_at: string | null;
  /** Payer marked the price as transferred; the payee's turn starts here. */
  payment_sent_at: string | null;
  payment_sent_by_user_id: string | null;
  settled_at: string | null;
  settled_on_behalf: boolean | null;
  /** Platform fee — Lexta-internal, stripped for creditors. The pct is
   *  fixed when the deal is made; the amount is that pct of Lexta's
   *  profit, booked when the case closes (null until then, and for good
   *  when there was no profit). */
  platform_fee_pct: number | null;
  platform_fee_amount: number | null;
  /** Lexta's profit the fee was assessed on, once the case closed. */
  platform_fee_basis: number | null;
  platform_fee_booked_at: string | null;
  /**
   * What a settlement would book right now: the per-offer override when
   * set, else the platform default. `null` on sell-backs and for
   * creditor principals. Settled offers carry the snapshot above.
   */
  platform_fee_pct_effective: number | null;
  platform_fee_is_override: boolean | null;
  platform_fee_estimate: number | null;
  events: OfferEvent[];
  payment_destination: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The figures locked onto a fixed-price offer when the operator bought:
 * the tabssaldo (principal − repaid) and the pct that gave the price.
 * `fee_pct` / `fee` are Lexta-internal and null for the creditor.
 */
export interface PriceSnapshot {
  /** The entry's own currency; every amount here is in it. */
  currency: string;
  tabssaldo: number | null;
  sale_pct: number;
  price: number;
  principal_amount: number | null;
  repaid_amount: number | null;
  quoted_at: string | null;
  /** The consent locked the price on `locked_at`; what the lender
   *  received until the buy is deducted; `price_payable` is the offer. */
  locked_at?: string | null;
  received_before_purchase?: number | null;
  price_payable?: number | null;
  lexta_share_before_purchase?: number | null;
  fee_pct: number | null;
  fee: number | null;
}

export interface AutoBuyPayload {
  /** Omit for the administrator's default. */
  settlement_mode?: SettlementMode;
  note?: string;
}

/**
 * Operator-only: buy a consented entry at the fixed price in one step
 * ("Vi køber"). The offer is created at today's quote and accepted in
 * the same write. Under `via_lender_account` ownership moves at once and
 * the offer is `collecting`; under `lump_sum` it parks in
 * `accepted_pending_settlement` until the lender confirms receipt.
 * 409 codes: auto_sale_disabled, consent_missing, not_priceable,
 * consent_pct_below_offer, entry_owned_by_lexta, offer_already_open.
 */
export async function autoBuyEntry(
  entryId: string,
  payload: AutoBuyPayload = {},
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/entries/${encodeURIComponent(entryId)}/auto-buy`,
    {
      settlement_mode: payload.settlement_mode ?? null,
      note: payload.note ?? null,
    },
  );
  return data;
}

/**
 * Compact row in the queue list. Mirrors the dispute-list shape:
 * the queue endpoint returns the bare minimum a table needs and
 * defers heavy fields (events, payment_destination) to the detail call.
 */
export interface PurchaseOfferListItem {
  id: string;
  entry_id: string;
  entry_subject_name: string | null;
  originating_lender_id: string;
  originating_lender_name: string | null;
  buyer_lender_name: string | null;
  direction: OfferDirection;
  /** Currency of every amount on the offer (the entry's own). */
  currency: string;
  current_amount: number;
  lexta_valuation: number | null;
  state: OfferState;
  settlement_mode: SettlementMode | null;
  auto_sale: boolean;
  settlement_payee: SettlementPayee | null;
  payment_sent_at: string | null;
  /** True when the caller has the next move — same logic as the nav badge. */
  awaiting_you: boolean;
  updated_at: string;
  created_at: string;
}

export interface PurchaseOfferListResponse {
  items: PurchaseOfferListItem[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface OpenOfferPayload {
  offer_amount: number;
  /** Operator-set Lexta valuation. Required by the backend for v1
   *  buy offers — captured at offer-open time so the operator commits
   *  to a debtor-side number before any acceptance can transfer
   *  ownership. */
  lexta_valuation: number;
  message?: string;
  /** Platform fee % for this purchase. Omit for the platform default; 0 waives it. */
  platform_fee_pct?: number;
  /** How Lexta proposes to settle. Lexta's call; defaults to `lump_sum`. */
  settlement_mode?: SettlementMode;
}

export interface CounterOfferPayload {
  counter_amount: number;
  /** Operator-only: optionally update the planned Lexta valuation
   *  during a counter. Lender counters that supply this have it
   *  silently dropped server-side. */
  lexta_valuation?: number;
  note?: string;
}

export interface SellbackOfferOpenPayload {
  buyer_lender_id: string;
  offer_amount: number;
  message?: string;
}

export interface GetPurchaseOffersParams {
  /** Filter to one or more states. Backend accepts repeated `?state=`. */
  state?: OfferState[];
  page?: number;
  page_size?: number;
}

/**
 * Operator-only: open a new purchase offer against a register entry.
 * Backend stamps the buyer-lender (Lexta Inkasso) from the operator's
 * configured destination; the SPA only sends amount and an optional
 * message. Settlement details (where the buyer pays the seller) are
 * worked out off-platform after the lender accepts — no payment
 * instruction is captured at the offer stage.
 */
export async function openPurchaseOffer(
  entryId: string,
  payload: OpenOfferPayload,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/entries/${entryId}/purchase-offers`,
    payload,
  );
  return data;
}

/**
 * Whoever's turn it is: post a counter offer. Switches the actor —
 * `pending_lender_response` → `pending_offerer_response` and back.
 */
export async function counterPurchaseOffer(
  id: string,
  payload: CounterOfferPayload,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/counter`,
    payload,
  );
  return data;
}

/**
 * Whoever's turn it is: accept the current offer on the terms it
 * carries (`settlement_mode`, set by Lexta). Only an operator may pass
 * `settlementMode`, to change it in the same step; a lender doing so
 * gets 403. Under `lump_sum` the offer parks until the seller confirms
 * receipt; under `via_lender_account` ownership moves at once and the
 * offer goes to `collecting`.
 */
export async function acceptPurchaseOffer(
  id: string,
  note?: string,
  settlementMode?: SettlementMode,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/accept`,
    { note: note ?? null, settlement_mode: settlementMode ?? null },
  );
  return data;
}

/**
 * Operator only: change how Lexta proposes to settle while the offer is
 * in negotiation. Lands on the timeline so the seller sees the terms.
 */
export async function setSettlementMode(
  id: string,
  settlementMode: SettlementMode,
  note?: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/settlement-mode`,
    { settlement_mode: settlementMode, note: note ?? null },
  );
  return data;
}

/**
 * Record a ledger line on a collecting offer. Seller only; an operator
 * may record on the seller's behalf with a note. A `received` line also
 * restarts the entry's auto-expire clock.
 */
export async function recordInstallment(
  id: string,
  body: {
    kind: InstallmentKind;
    amount: number;
    received_at?: string | null;
    note?: string | null;
  },
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/installments`,
    {
      kind: body.kind,
      amount: body.amount,
      received_at: body.received_at ?? null,
      note: body.note ?? null,
    },
  );
  return data;
}

/** Strike a ledger line; the row stays, marked removed. Reason required. */
export async function removeInstallment(
  id: string,
  installmentId: string,
  reason: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/installments/${installmentId}/remove`,
    { reason },
  );
  return data;
}

/** Write a message on the offer timeline (the conversation box). */
export async function addOfferNote(
  id: string,
  note: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/notes`,
    { note },
  );
  return data;
}

/**
 * The payee confirms the purchase price has arrived. THIS is the step
 * that transfers ownership (accept only parks the offer). An operator
 * confirming on the lender's behalf must supply a note.
 */
export async function confirmSettlement(
  id: string,
  note?: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/confirm-settlement`,
    { note: note ?? null },
  );
  return data;
}

/**
 * The payer records that the purchase price has been transferred to
 * the seller (off-platform). Hands the turn to the payee; does not
 * move ownership.
 */
export async function markPaymentSent(
  id: string,
  note?: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/mark-paid`,
    { note: note ?? null },
  );
  return data;
}

/** How many live offers need the caller's next move (nav badge). */
export async function getAwaitingOffersCount(): Promise<number> {
  const { data } = await api.get<{ count: number }>(
    '/purchase-offers/awaiting-count',
  );
  return data.count;
}

export interface PlatformFeeBucket {
  month: string;
  transfers: number;
  volume: number;
  fee_total: number;
}

export interface PlatformFeeSummary {
  fee_pct: number;
  /** The currency the buckets are summed in; never mixed. */
  currency: MoneyCurrency;
  /** Every currency with a booked fee, DKK first and always. */
  currencies: MoneyCurrency[];
  current_month: PlatformFeeBucket;
  all_time: PlatformFeeBucket;
  months: PlatformFeeBucket[];
}

/** Operator-only ledger of the platform fee accrued on settled purchases. */
export async function getPlatformFeeSummary(
  currency: MoneyCurrency = 'DKK',
): Promise<PlatformFeeSummary> {
  const { data } = await api.get<PlatformFeeSummary>(
    '/purchase-offers/platform-fees',
    { params: { currency } },
  );
  return data;
}

export type FeeState = 'open' | 'invoiced' | 'paid';

/** One closed case in the fee ledger — the fee is what the operator who
 *  ran the case owes Lexta; `fee_state` is the off-platform bookkeeping. */
export interface PlatformFeeLedgerRow {
  /** The row id: an offer, or an agreement when `kind` is `agreement`. */
  offer_id: string;
  kind: 'purchase' | 'agreement';
  /** Lexta's profit on the case — what the fee is the pct of. Null on
   *  rows booked before the one fee rule. */
  fee_basis: number | null;
  entry_id: string;
  entry_subject_name: string | null;
  seller_lender_id: string;
  seller_lender_name: string | null;
  settled_at: string | null;
  /** Currency of every amount on the row (the case's own). */
  currency: string;
  accepted_amount: number;
  platform_fee_pct: number;
  platform_fee_amount: number;
  fee_state: FeeState;
  invoiced_at: string | null;
  paid_at: string | null;
  /** The operator who ran the case; null on rows without one. */
  operator_user_id: string | null;
  operator_name: string | null;
}

export interface PlatformFeeLedgerTotals {
  count: number;
  fee_total: number;
  open_total: number;
  invoiced_total: number;
  paid_total: number;
}

/** One operator's share of the month; `outstanding_total` = open + invoiced. */
export interface PlatformFeeOperatorTotals extends PlatformFeeLedgerTotals {
  operator_user_id: string | null;
  operator_name: string | null;
  outstanding_total: number;
}

export interface PlatformFeeLedger {
  month: string | null;
  currency: MoneyCurrency;
  /** Every currency with a booked fee, DKK first and always. */
  currencies: MoneyCurrency[];
  /** Every settlement month with a booked fee, newest first. */
  months: string[];
  rows: PlatformFeeLedgerRow[];
  totals: PlatformFeeLedgerTotals;
  /** Per-operator split of the month, not narrowed by the operator
   *  filter; largest outstanding first. */
  by_operator: PlatformFeeOperatorTotals[];
}

function ledgerParams(
  month?: string | null,
  operatorUserId?: string | null,
  currency?: MoneyCurrency,
) {
  const params: Record<string, string> = {};
  if (currency) params.currency = currency;
  if (month) params.month = month;
  if (operatorUserId) params.operator_user_id = operatorUserId;
  return Object.keys(params).length ? params : undefined;
}

/** Operator-only: the fee ledger, optionally for one `YYYY-MM` month and
 *  one operator (`'me'` = the caller). */
export async function getPlatformFeeLedger(
  month?: string | null,
  operatorUserId?: string | null,
  currency?: MoneyCurrency,
): Promise<PlatformFeeLedger> {
  const { data } = await api.get<PlatformFeeLedger>(
    '/purchase-offers/platform-fees/ledger',
    { params: ledgerParams(month, operatorUserId, currency) },
  );
  return data;
}

/** Operator-only: the ledger as a CSV file (semicolon-separated, BOM). */
export async function downloadPlatformFeeLedgerCsv(
  month?: string | null,
  operatorUserId?: string | null,
  currency?: MoneyCurrency,
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    '/purchase-offers/platform-fees/ledger.csv',
    { params: ledgerParams(month, operatorUserId, currency), responseType: 'blob' },
  );
  return data;
}

/**
 * Admin-only: move settled purchases between bookkeeping states.
 * `paid` implies `invoiced`; `open` undoes both. All-or-nothing on
 * validation.
 */
export async function markPlatformFees(
  offerIds: string[],
  state: FeeState,
  note?: string,
): Promise<PlatformFeeLedgerRow[]> {
  const { data } = await api.post<{ rows: PlatformFeeLedgerRow[] }>(
    '/purchase-offers/platform-fees/mark',
    { offer_ids: offerIds, state, note },
  );
  return data.rows;
}

/** Operator-only: the platform-wide default fee percentage on purchases. */
export interface PlatformFeeDefault {
  fee_pct: number;
}

export async function getPlatformFeeDefault(): Promise<PlatformFeeDefault> {
  const { data } = await api.get<PlatformFeeDefault>(
    '/purchase-offers/platform-fee-default',
  );
  return data;
}

/**
 * Operator-only: change the default fee percentage. Applies to every
 * purchase without a per-offer override from the next settlement on.
 */
export async function updatePlatformFeeDefault(
  feePct: number,
): Promise<PlatformFeeDefault> {
  const { data } = await api.put<PlatformFeeDefault>(
    '/purchase-offers/platform-fee-default',
    { fee_pct: feePct },
  );
  return data;
}

// ---------------------------------------------------------------------
// Platform settings (auto-sale design, 2026-09-21)
// ---------------------------------------------------------------------
//
//   GET /platform-settings   operator — every setting with its effective value
//   PUT /platform-settings   admin    — partial update, audited as one row

export type DefaultSettlementMode = SettlementMode;

export interface PlatformSettings {
  /** Platform fee, % of the fee base (price on negotiated deals,
   *  tabssaldo on auto-sale deals). */
  purchase_fee_pct: number;
  /** Auto-sale price, % of tabssaldo (principal − repaid). */
  sale_pct: number;
  /** Whether the one-click buy is offered at all. */
  auto_sale_enabled: boolean;
  /** Whether operators may open free-price buy offers. */
  negotiation_allowed: boolean;
  /** Days from "påbegyndt" to the agreement deadline on a collecting case. */
  default_frist_days: number;
  /** How a one-click buy settles when the operator does not choose. */
  default_settlement_mode: DefaultSettlementMode;
  /** The payment type dropdown (Betalingstyper); `other` besides. */
  payment_method_types: PaymentMethodType[];
  /** Resolved server-side per request; decides whether inputs render. */
  viewer_is_admin: boolean;
}

export type PlatformSettingsUpdate = Partial<
  Omit<PlatformSettings, 'viewer_is_admin' | 'payment_method_types'>
> & {
  /** The WHOLE list, in order; a row left out is retired. */
  payment_method_types?: PaymentMethodTypeInput[];
  note?: string;
};

export async function getPlatformSettings(): Promise<PlatformSettings> {
  const { data } = await api.get<PlatformSettings>('/platform-settings');
  return data;
}

/** Admin-only. Only the keys present are changed. */
export async function updatePlatformSettings(
  changes: PlatformSettingsUpdate,
): Promise<PlatformSettings> {
  const { data } = await api.put<PlatformSettings>('/platform-settings', changes);
  return data;
}

/**
 * Operator-only: set the platform fee for one live purchase. `0` waives
 * it, `null` returns the offer to the platform default.
 */
export async function setOfferPlatformFee(
  offerId: string,
  platformFeePct: number | null,
  note?: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${offerId}/platform-fee`,
    { platform_fee_pct: platformFeePct, note },
  );
  return data;
}

/**
 * Whoever's turn it is: reject the offer outright. Terminal.
 */
export async function rejectPurchaseOffer(
  id: string,
  note?: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/reject`,
    { note: note ?? null },
  );
  return data;
}

/**
 * Current actor: withdraw your own pending offer / counter. Terminal.
 * Body intentionally empty — backend infers actor from JWT.
 */
export async function withdrawPurchaseOffer(
  id: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/purchase-offers/${id}/withdraw`,
  );
  return data;
}

/**
 * Role-scoped queue. Operator → all offers; lender_human → offers on
 * entries they own. State filter is sent as a repeated `?state=` query
 * parameter; axios's default array serializer (`?state[]=`) is not
 * recognised by the backend.
 */
export async function getPurchaseOffers(
  params: GetPurchaseOffersParams = {},
): Promise<PurchaseOfferListResponse> {
  const { data } = await api.get<PurchaseOfferListResponse>(
    '/purchase-offers',
    {
      params: {
        state: params.state,
        page: params.page,
        page_size: params.page_size,
      },
      paramsSerializer: { indexes: null },
    },
  );
  return data;
}

export async function getPurchaseOfferById(
  id: string,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.get<PurchaseOfferResponse>(
    `/purchase-offers/${id}`,
  );
  return data;
}

/**
 * Operator-only: open a sell-back offer on a Lexta-owned entry.
 * Lexta is the seller; the chosen `buyer_lender_id` lender is the
 * responder. No `lexta_valuation` is captured at open time — it
 * already lives on the entry.
 */
export async function openSellbackOffer(
  entryId: string,
  payload: SellbackOfferOpenPayload,
): Promise<PurchaseOfferResponse> {
  const { data } = await api.post<PurchaseOfferResponse>(
    `/entries/${entryId}/sellback-offers`,
    payload,
  );
  return data;
}

// =====================================================================
// Per-lender account (auto-sale design, 2026-09-21, decision 12)
// =====================================================================
//
//   GET  /lenders/{id}/account                 operator, or the lender's own human
//   POST /lenders/{id}/account/months/{YYYY-MM} operator: "markér som betalt"
//   GET  /lenders/account-rollup?lender_id=    operator: every lender, summed
//
// Every figure is derived from the entries and the via-lender-account
// ledgers; nothing is stored twice. Monthly settlement is manual: the
// statement shows what the lender holds for Lexta, the lender transfers,
// an operator marks the month paid.

export interface LenderAccountMonth {
  month: string;
  received: number;
  lender_share: number;
  /** What the lender collected for Lexta that month. */
  lexta_share: number;
  /** What the lender recorded as paid over to Lexta that month. */
  forwarded: number;
  paid_marked_at: string | null;
  paid_marked_by_user_id: string | null;
  paid_note: string | null;
}

export interface LenderAccount {
  lender_id: string;
  lender_name: string | null;
  currency: MoneyCurrency;
  /** Every currency this lender has data in, DKK first and always. */
  currencies: MoneyCurrency[];
  entries_filed: number;
  active_count: number;
  total_principal: number;
  total_tabssaldo: number;
  sold_to_lexta_count: number;
  collecting_count: number;
  shelved_count: number;
  received_total: number;
  lender_share_total: number;
  forwarded_total: number;
  /** What the lender holds for Lexta right now. */
  held_for_lexta: number;
  /** What the lender is still owed of the agreed prices — lender first, always. */
  lender_outstanding_total: number;
  months: LenderAccountMonth[];
}

export interface AccountRollupTotals {
  entries_filed: number;
  active_count: number;
  sold_to_lexta_count: number;
  collecting_count: number;
  shelved_count: number;
  total_principal: number;
  total_tabssaldo: number;
  received_total: number;
  lender_share_total: number;
  forwarded_total: number;
  held_for_lexta: number;
  lender_outstanding_total: number;
}

export interface AccountRollup {
  lenders: LenderAccount[];
  totals: AccountRollupTotals;
  currency: MoneyCurrency;
  /** Every currency any lender has data in, DKK first and always. */
  currencies: MoneyCurrency[];
}

export async function getLenderAccount(
  lenderId: string,
  currency: MoneyCurrency = 'DKK',
): Promise<LenderAccount> {
  const { data } = await api.get<LenderAccount>(
    `/lenders/${encodeURIComponent(lenderId)}/account`,
    { params: { currency } },
  );
  return data;
}

/** Operator only: mark (or unmark) a statement month as settled by the lender. */
export async function markAccountMonth(
  lenderId: string,
  month: string,
  paid: boolean,
  note?: string,
  currency: MoneyCurrency = 'DKK',
): Promise<LenderAccount> {
  const { data } = await api.post<LenderAccount>(
    `/lenders/${encodeURIComponent(lenderId)}/account/months/${encodeURIComponent(month)}`,
    { paid, note: note ?? null },
    { params: { currency } },
  );
  return data;
}

/** Operator only: every lender's account figures, summed; optionally one lender. */
export async function getAccountRollup(
  lenderId?: string | null,
  currency: MoneyCurrency = 'DKK',
): Promise<AccountRollup> {
  const { data } = await api.get<AccountRollup>('/lenders/account-rollup', {
    params: { currency, ...(lenderId ? { lender_id: lenderId } : {}) },
  });
  return data;
}

// =====================================================================
// Lexta valuation / settlement / paid_off — operator-only
// =====================================================================

/**
 * Per-row shape inside `EntryDetail.previous_owners`. Each transfer
 * appends one row; `settled_at` flips from null when the operator
 * confirms the off-platform wire has cleared. `valuation_snapshot`
 * + `amount_paid_snapshot` are populated only on sell-back rows
 * (capturing the Lexta-side state at the moment ownership left
 * Lexta).
 */
export interface PreviousOwner {
  originating_lender_id: string | null;
  creditor_contact: CreditorContact | null;
  payment_destination: string | null;
  transferred_at: string;
  transfer_amount: number;
  settled_at: string | null;
  settled_by_user_id: string | null;
  valuation_snapshot: number | null;
  amount_paid_snapshot: number | null;
  platform_fee_pct: number | null;
  platform_fee_amount: number | null;
}

export interface UpdateEntryValuationPayload {
  lexta_valuation: number;
  note: string;
}

export interface EntryValuationResponse {
  entry_id: string;
  lexta_valuation: number;
  lexta_amount_paid: number;
  lexta_residual: number;
  updated_at: string;
}

export async function updateEntryValuation(
  entryId: string,
  payload: UpdateEntryValuationPayload,
): Promise<EntryValuationResponse> {
  const { data } = await api.post<EntryValuationResponse>(
    `/entries/${entryId}/lexta-valuation`,
    payload,
  );
  return data;
}

export interface PreviousOwnerSettleResponse {
  entry_id: string;
  owner_index: number;
  settled_at: string;
  settled_by_user_id: string | null;
  transferred_at: string;
  transfer_amount: number;
  originating_lender_id: string | null;
}

export async function markPreviousOwnerSettled(
  entryId: string,
  ownerIndex: number,
): Promise<PreviousOwnerSettleResponse> {
  const { data } = await api.post<PreviousOwnerSettleResponse>(
    `/entries/${entryId}/previous-owners/${ownerIndex}/settle`,
  );
  return data;
}

export async function forceClosePaidOff(
  entryId: string,
  note: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${entryId}/paid-off`,
    { note },
  );
  return data;
}

// =====================================================================
// Register entry amendments
// =====================================================================
//
// `PATCH /api/v1/register/entries/{id}` — update mutable fields on an
// existing register entry. All fields are optional; the backend applies
// only the keys that are present in the JSON body. Returns the full
// `EntryDetail` shape (same as `closeEntry` / `recordPaymentActivity`).
//
// Tiers (services/register.py): the three totals — `principal_amount`,
// `repaid_amount`, `debt_amount` — may be corrected by the owning lender
// or an operator, always with `reason` (422 `amend_reason_required`
// otherwise); `currency`, `loan_disbursed_at`, `default_since` and
// `originated_from` are operator-only (403 `amend_not_allowed`).

/**
 * Fields that can be amended on an existing register entry.
 *
 * All fields are optional — the PATCH applies only keys present in the
 * body. The backend validates each field independently and returns the
 * updated `EntryDetail`. See the tier note above for who may send what.
 */
export interface AmendEntryPayload {
  subject_name?: string;
  subject_address?: string;
  subject_phone?: string;
  subject_email?: string;
  subject_postal_code?: string;
  subject_city?: string;
  subject_facebook_url?: string;
  /** ISO date or the first six CPR digits; the backend normalises. */
  subject_birth_date?: string;
  creditor_contact?: { name: string; email: string; phone?: string };
  payment_destination?: string;
  /** Point the debtor to one of the payee's payment methods
   *  (`getEntryPaymentMethods`). Never with `payment_destination`. */
  payment_method_id?: string;
  add_history_note?: string;
  /** Free text from the lender about the subject, replaced whole. Empty string clears. */
  notes?: string | null;
  /** Lexta's notepad, operator-only. Replaced whole; empty string clears. */
  operator_notes?: string | null;
  /** Standing consent to a fixed-price sale to Lexta: true grants
   *  (snapshotting today's sale pct), false withdraws. Owner or
   *  operator, no reason required. */
  auto_sale_consent?: boolean;
  /** Inline document stubs to attach. Uses the same `type`/`filename`/`url`
   *  triad as `RegisterDocument` on the open-entry form. `url` may be null
   *  for stubs that have not yet been uploaded. */
  add_documents?: Array<{ type: string; filename: string; url?: string | null }>;
  /** Operator-only: `Document.url`s to detach from the entry. Requires
   *  `reason`. The blob stays in the archive; the removal is audited. */
  remove_document_urls?: string[];
  // The three totals — owner or operator, `reason` required.
  principal_amount?: number;
  repaid_amount?: number;
  /** The lender's stated current claim. Setting it on a pre-split entry
   *  itemises it (the backend derives `fees_amount`). */
  debt_amount?: number;
  // Operator-only — UI must hide these for non-operator principals.
  currency?: Currency;
  loan_disbursed_at?: string;  // YYYY-MM-DD
  default_since?: string;      // YYYY-MM-DD
  originated_from?: string;
  reason?: string;
}

/**
 * Amend mutable fields on an existing register entry.
 *
 * Sends a PATCH to `/register/entries/{entryId}` with only the supplied
 * fields. Returns the full updated `EntryDetail`.
 */
export async function amendEntry(
  entryId: string,
  payload: AmendEntryPayload,
): Promise<EntryDetail> {
  const { data } = await api.patch<EntryDetail>(
    `/register/entries/${encodeURIComponent(entryId)}`,
    payload,
  );
  return data;
}

/**
 * Remove one note from an entry's case history. `noteId` is
 * `EntryDetail.history[].id`. Operators may remove on any entry, the
 * owning lender on its own; the text stays in the audit trail.
 */
export async function removeHistoryNote(
  entryId: string,
  noteId: string,
  reason?: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${encodeURIComponent(entryId)}/history-notes/remove`,
    { note_id: noteId, reason: reason ?? null },
  );
  return data;
}

// =====================================================================
// Attach CPR to an incomplete (CPR-less) register entry
// =====================================================================
//
// Three endpoints, all under `/register/entries/{id}/…` or
// `/operator/register/entries/{id}/…`:
//
//   GET  /register/entries/{id}/attach-cpr/preview?cpr=<cpr>
//        — dry-run: returns case ("A" | "B") and how many existing
//          entries the target subject already has. No writes.
//   POST /register/entries/{id}/attach-cpr  { cpr }
//        — commits the CPR attachment; returns the updated EntryDetail.
//   POST /operator/register/entries/{id}/revert-cpr-attachment
//        — operator-only: undo a prior attach-CPR; returns the updated
//          EntryDetail with `is_incomplete=true` and the CPR stripped.

/**
 * Dry-run response for the attach-CPR preview call.
 *
 * `case` drives which confirmation copy is shown in the UI:
 *   "A" — the CPR maps to a new (currently unknown) subject: the entry
 *         will be the only one for that person.
 *   "B" — the CPR maps to a subject that already has register entries;
 *         `existing_entries_count_at_target` tells the operator how many.
 */
export interface AttachCprPreviewResponse {
  case: 'A' | 'B';
  existing_entries_count_at_target: number;
}

/**
 * Dry-run: check what would happen if `cpr` were attached to this entry.
 * Returns the case ("A" | "B") and the current entry count at the target
 * subject. Makes no writes.
 */
export async function attachCprPreview(
  entryId: string,
  number: string,
  country: IdCountry = 'DK',
): Promise<AttachCprPreviewResponse> {
  const params =
    country === 'DK' ? { cpr: number } : { national_id: number, id_country: country };
  const { data } = await api.get<AttachCprPreviewResponse>(
    `/register/entries/${encodeURIComponent(entryId)}/attach-cpr/preview`,
    { params },
  );
  return data;
}

/**
 * Attach a CPR to an incomplete (CPR-less) register entry. The entry's
 * `subject_key` is repointed to the SHA-256 of the supplied CPR and
 * `is_incomplete` is cleared. Returns the full updated `EntryDetail`.
 */
export async function attachCpr(
  entryId: string,
  number: string,
  country: IdCountry = 'DK',
): Promise<EntryDetail> {
  const body =
    country === 'DK' ? { cpr: number } : { national_id: number, id_country: country };
  const { data } = await api.post<EntryDetail>(
    `/register/entries/${encodeURIComponent(entryId)}/attach-cpr`,
    body,
  );
  return data;
}

/**
 * Operator-only: revert a prior CPR attachment. Strips the CPR link,
 * reinstates `is_incomplete=true`, and re-anchors the `subject_key` to
 * the pre-attach placeholder. Returns the full updated `EntryDetail`.
 */
export async function revertCprAttachment(
  entryId: string,
): Promise<EntryDetail> {
  const { data } = await api.post<EntryDetail>(
    `/operator/register/entries/${encodeURIComponent(entryId)}/revert-cpr-attachment`,
  );
  return data;
}

// =====================================================================
// Collection handler — which operator is working an entry
// =====================================================================
//
// Operator-only. `start` answers 409 `collection_in_progress` (the
// detail carries `collection_handler` naming the holder) while another
// operator has the entry, unless `takeover_reason` is given. `release`
// by anyone but the holder needs a `reason` (422
// `collection_reason_required`).

export interface CollectionHandlerResponse {
  entry_id: string;
  collection_handler: CollectionHandler | null;
}

export async function startCollection(
  entryId: string,
  payload: { note?: string | null; takeover_reason?: string | null } = {},
): Promise<CollectionHandlerResponse> {
  const { data } = await api.post<CollectionHandlerResponse>(
    `/operator/register/entries/${encodeURIComponent(entryId)}/collection/start`,
    { note: payload.note ?? null, takeover_reason: payload.takeover_reason ?? null },
  );
  return data;
}

/** "Forlæng frist" on the marker. Note required; the new deadline in
 *  the future. */
export async function extendCollectionDeadline(
  entryId: string,
  payload: { deadline_at: string; note: string },
): Promise<CollectionHandlerResponse> {
  const { data } = await api.post<CollectionHandlerResponse>(
    `/operator/register/entries/${encodeURIComponent(entryId)}/collection/deadline`,
    payload,
  );
  return data;
}

export async function releaseCollection(
  entryId: string,
  payload: { reason?: string | null } = {},
): Promise<CollectionHandlerResponse> {
  const { data } = await api.post<CollectionHandlerResponse>(
    `/operator/register/entries/${encodeURIComponent(entryId)}/collection/release`,
    { reason: payload.reason ?? null },
  );
  return data;
}

// =====================================================================
// Per-entry audit history
// =====================================================================
//
// Thin wrapper around the existing `/audit-log` endpoint filtered to a
// single entry. Returns a lightweight `AuditRow` shape — a strict subset
// of `AuditLogEntry` — so callers don't need to import the full audit-log
// type just to render an entry timeline.

/**
 * Lightweight projection of one audit-log row, scoped to a single
 * register entry. A strict subset of `AuditLogEntry` — only the fields
 * the entry-detail timeline needs are promoted to top-level here; the
 * rest stay in `extra` on the full `AuditLogEntry` type.
 *
 * `diff` is populated by the backend on `register.amended` rows; each
 * key is a field name whose value changed, with `before` and `after`
 * snapshots.
 */
export interface AuditRow {
  action: string;
  at: string;
  entry_id?: string;
  actor_kind?: string;
  actor_lender_id?: string;
  actor_user_id?: string;
  /** Resolved server-side: operator name / e-mail or lender display name. */
  actor_label?: string | null;
  /** Admin "log ind som" mark, as on `AuditLogEntry`. The first four are
   *  sent to operators only; everyone else gets `admin_intervention`
   *  alone. When a mark is present `actor_*` is the user the administrator
   *  acted as. */
  impersonator_user_id?: string;
  impersonation_id?: string;
  impersonator_label?: string | null;
  impersonated_role?: string;
  admin_intervention?: boolean;
  diff?: Record<string, { before: unknown; after: unknown }>;
  /** Writer-specific payload (`removed_text`, `note`, …). */
  extra?: Record<string, unknown>;
  reason?: string | null;
  amount?: number;
}

/**
 * Fetch the audit history for a single register entry.
 *
 * Calls `GET /audit-log?entry_id=<id>` (same endpoint as `listAuditLog`)
 * and returns `response.items` cast to `AuditRow[]`. The backend already
 * scopes results to the caller's visible entries, so no extra auth logic
 * is needed here.
 */
export async function getEntryAuditHistory(entryId: string): Promise<AuditRow[]> {
  const { data } = await api.get<AuditLogListResponse>('/audit-log', {
    params: { entry_id: entryId },
  });
  // The audit read flattens only the named reference fields; everything
  // a writer added — `diff`, `reason`, `amount`, `actor_kind` — sits in
  // `extra`. Lift the ones the timeline renders so a lender can see what
  // an operator changed on its own entry.
  return data.items.map((item) => {
    const extra = item.extra ?? {};
    const diff = extra.diff;
    return {
      action: item.action,
      at: item.at,
      entry_id: item.entry_id ?? undefined,
      actor_user_id: item.actor_user_id ?? undefined,
      actor_label: item.actor_label,
      // The admin mark is top-level on the read, not in `extra`. Operators
      // get all five; everyone else only `admin_intervention`.
      impersonator_user_id: item.impersonator_user_id ?? undefined,
      impersonation_id: item.impersonation_id ?? undefined,
      impersonator_label: item.impersonator_label,
      impersonated_role: item.impersonated_role ?? undefined,
      admin_intervention: item.admin_intervention === true,
      actor_kind: typeof extra.actor_kind === 'string' ? extra.actor_kind : undefined,
      actor_lender_id:
        typeof extra.actor_lender_id === 'string' ? extra.actor_lender_id : undefined,
      diff:
        diff && typeof diff === 'object' && !Array.isArray(diff)
          ? (diff as AuditRow['diff'])
          : undefined,
      reason: typeof extra.reason === 'string' ? extra.reason : null,
      amount: typeof extra.amount === 'number' ? extra.amount : undefined,
      extra,
    } satisfies AuditRow;
  });
}

// =====================================================================
// Creditor links (platform attribution)
// =====================================================================
//
// A `platform` lender (e.g. Lendora) files entries on behalf of the
// real creditor via `on_behalf_of` (see
// docs/superpowers/specs/2026-09-15-creditor-attribution-design.md).
// Ownership only moves to the creditor's own Lexta account once she
// accepts a `creditor_links` row — either because Lexta found her by
// e-mail (`GET /creditor-links/pending`) or because the platform
// handed her a one-time connect code (`/connect/:code`).

export type LinkStatus = 'pending' | 'active' | 'revoked' | 'declined' | 'superseded';

/** Resolution of a filed entry's beneficial creditor — mirrors
 *  `beneficial_creditor.resolution` on `InventoryEntry` / `EntryDetail`. */
export type BeneficialCreditorResolution = 'linked' | 'pending' | 'unresolved';

/** Who a platform says actually holds a claim it filed. Carried on
 *  `InventoryEntry.beneficial_creditor` / `EntryDetail.beneficial_creditor`. */
export interface BeneficialCreditorView {
  external_ref: string;
  display_name: string;
  email: string | null;
  phone: string | null;
  resolution: BeneficialCreditorResolution;
  resolved_lender_id: string | null;
  platform_lender_id: string;
}

/**
 * A `creditor_links` row as the API projects it. `connect_url` is only
 * populated on the handful of calls that just (re)issued a code
 * (`previewConnectCode` never sets it; operator/creditor reads don't
 * either) — treat its absence as "no fresh code", not as an error.
 */
export interface CreditorLinkView {
  id: string;
  status: LinkStatus;
  platform_lender_id: string;
  platform_lender_name: string;
  external_ref: string;
  display_name: string;
  email: string | null;
  phone: string | null;
  lexta_lender_id: string | null;
  lexta_lender_name: string | null;
  candidate_lender_id: string | null;
  candidate_found: boolean;
  scopes: string[];
  entries_count: number;
  entries_active_count: number;
  total_debt: number;
  entries_transferred: number;
  /** Entries the accept could not carry over — under negotiation with
   *  another creditor, or already present on the accepting lender's
   *  account. Defaults to 0 for links accepted before this field
   *  existed. */
  entries_skipped: number;
  connect_url: string | null;
  connect_code_expires_at: string | null;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
  revoked_at: string | null;
  declined_at: string | null;
  /** A Lexta administrator logged in as the creditor took the step that
   *  put the link in its CURRENT state (accepted, declined or revoked it).
   *  ADMIN-INDGREB. An admin-accepted link the creditor later revoked
   *  reads false here; the per-step flags below keep that accept visible.
   *  None of the four names the administrator. Absent on older responses. */
  admin_intervention?: boolean;
  activated_admin_intervention?: boolean;
  declined_admin_intervention?: boolean;
  revoked_admin_intervention?: boolean;
}

/** Why `GET /creditor-links/connect/{code}` says the viewer can't
 *  accept a pending link — see `ConnectPreview.refusal_reason`. */
export type ConnectRefusalReason =
  | 'not_pending'
  | 'candidate_mismatch'
  | 'platform_cannot_adopt'
  | 'lender_not_approved'
  /** Operator looked without naming the lender who accepts. */
  | 'acting_lender_required';

/** Response of `GET /creditor-links/connect/{code}` — the `/connect/:code`
 *  page's data source. */
export interface ConnectPreview {
  link: CreditorLinkView;
  candidate_is_you: boolean;
  can_accept: boolean;
  refusal_reason: ConnectRefusalReason | null;
}

/** `lender_human`: links `pending` with `candidate_lender_id == me` —
 *  the "Krav til overtagelse" banner on `/entries`. */
export async function getPendingCreditorLinks(): Promise<CreditorLinkView[]> {
  const { data } = await api.get<CreditorLinkView[]>('/creditor-links/pending');
  return data;
}

/** `lender_human`: how many pending links await this lender's decision
 *  — the header badge on "Poster", polled like the offers-awaiting count. */
export async function getPendingCreditorLinkCount(): Promise<number> {
  const { data } = await api.get<{ count: number }>('/creditor-links/pending-count');
  return data.count;
}

/** `lender_human`: links with `lexta_lender_id == me` (active + revoked)
 *  — the "Forbundne platforme" section on `/profile`. */
export async function getMyCreditorLinks(): Promise<CreditorLinkView[]> {
  const { data } = await api.get<CreditorLinkView[]>('/creditor-links/mine');
  return data;
}

/** `lender_human`: preview a connect code before accepting it. 404
 *  `connect_code_invalid` when the code is unknown or expired. */
export async function previewConnectCode(
  code: string,
  actingAsLenderId?: string,
): Promise<ConnectPreview> {
  const { data } = await api.get<ConnectPreview>(
    `/creditor-links/connect/${encodeURIComponent(code)}`,
    { params: actingAsLenderId ? { acting_as_lender_id: actingAsLenderId } : undefined },
  );
  return data;
}

/** `lender_human`: accept a link by its one-time connect code. */
export async function acceptConnectCode(
  code: string,
  actingAsLenderId?: string,
): Promise<CreditorLinkView> {
  const { data } = await api.post<CreditorLinkView>(
    `/creditor-links/connect/${encodeURIComponent(code)}/accept`,
    actingAsLenderId ? { acting_as_lender_id: actingAsLenderId } : {},
  );
  return data;
}

/** `lender_human`, candidate: accept a pending link found by e-mail
 *  match (no connect code needed — see `getPendingCreditorLinks`). */
export async function acceptCreditorLink(
  id: string,
  actingAsLenderId?: string,
): Promise<CreditorLinkView> {
  const { data } = await api.post<CreditorLinkView>(
    `/creditor-links/${encodeURIComponent(id)}/accept`,
    actingAsLenderId ? { acting_as_lender_id: actingAsLenderId } : {},
  );
  return data;
}

/** `lender_human`, candidate: decline a pending link. The platform may
 *  request again later (a fresh pending row on the next filing). */
export async function declineCreditorLink(id: string): Promise<CreditorLinkView> {
  const { data } = await api.post<CreditorLinkView>(
    `/creditor-links/${encodeURIComponent(id)}/decline`,
  );
  return data;
}

/** `lender_human` creditor or operator: revoke an active link. Entries
 *  stay with the creditor; the platform loses `manage` immediately. */
export async function revokeCreditorLink(id: string): Promise<CreditorLinkView> {
  const { data } = await api.post<CreditorLinkView>(
    `/creditor-links/${encodeURIComponent(id)}/revoke`,
  );
  return data;
}

/** Operator: the full link directory, optionally filtered. */
export async function listCreditorLinks(params: {
  status?: LinkStatus;
  platform_lender_id?: string;
} = {}): Promise<CreditorLinkView[]> {
  const { data } = await api.get<CreditorLinkView[]>('/creditor-links', {
    params: {
      status: params.status || undefined,
      platform_lender_id: params.platform_lender_id || undefined,
    },
  });
  return data;
}

// =====================================================================
// Lender API keys (machine-to-machine integration credentials)
// =====================================================================
//
// Lender machines authenticate to Lexta via `X-Lender-Email` +
// `X-Lender-Api-Key` headers. The API-keys page lets a lender admin
// (or an operator on their behalf) view, issue, and revoke keys for a
// given lender.
//
// Plaintext is returned EXACTLY ONCE on issuance, like a GitHub PAT.
// The SPA must surface it once and never persist it (no react-query
// cache, no localStorage). After the disclosure card is dismissed the
// plaintext is unrecoverable — operators have to issue a new key.

/**
 * Public projection of a single API key. Plaintext is NEVER part of
 * this shape — only the metadata. The plaintext only appears in the
 * one-shot `IssueKeyResponse` returned by `issueApiKey`.
 */
export type ApiKeyScope = 'lookup' | 'file' | 'manage';
export const API_KEY_SCOPES: ApiKeyScope[] = ['lookup', 'file', 'manage'];

export interface ApiKeySummary {
  id: string;
  note: string | null;
  /** What the key may do: `lookup` (POST /lookup), `file` (create
   *  entries, claims and creditor links), `manage` (every other
   *  write). Keys issued before scopes existed carry all three. */
  scopes: ApiKeyScope[];
  /** ISO timestamp after which the key stops authenticating, or null
   *  for no expiry. */
  expires_at: string | null;
  created_at: string;
  /** ISO timestamp the key was revoked at, or null if still active. */
  revoked_at: string | null;
  /** ISO timestamp of the most recent successful auth using this key,
   *  or null if it has never been used. */
  last_used_at: string | null;
  /** Convenience flag — not revoked and not expired. The backend
   *  computes this so the SPA doesn't have to second-guess the
   *  revocation / expiry semantics. */
  is_active: boolean;
}

export interface ListKeysResponse {
  lender_id: string;
  lender_display_name: string;
  keys: ApiKeySummary[];
}

export interface IssueKeyResponse {
  key: ApiKeySummary;
  /** The plaintext API key (`lxt_…`). Returned ONLY by this endpoint
   *  and never persisted server-side in plaintext. */
  plaintext: string;
}

export async function listApiKeys(
  lenderId: string,
): Promise<ListKeysResponse> {
  const { data } = await api.get<ListKeysResponse>(
    `/lenders/${encodeURIComponent(lenderId)}/api-keys`,
  );
  return data;
}

export interface IssueKeyOptions {
  note?: string;
  /** Defaults to all three scopes when omitted. */
  scopes?: ApiKeyScope[];
  /** 1–3650; omitted = never expires. */
  expires_in_days?: number;
}

export async function issueApiKey(
  lenderId: string,
  options: IssueKeyOptions = {},
): Promise<IssueKeyResponse> {
  const { data } = await api.post<IssueKeyResponse>(
    `/lenders/${encodeURIComponent(lenderId)}/api-keys`,
    {
      note: options.note ?? null,
      scopes: options.scopes ?? null,
      expires_in_days: options.expires_in_days ?? null,
    },
  );
  return data;
}

export async function revokeApiKey(
  lenderId: string,
  keyId: string,
): Promise<ApiKeySummary> {
  const { data } = await api.post<ApiKeySummary>(
    `/lenders/${encodeURIComponent(lenderId)}/api-keys/${encodeURIComponent(keyId)}/revoke`,
  );
  return data;
}

// =====================================================================
// Audit log (operator-only read of every audit row across all flows)
// =====================================================================
//
// Backed by `GET /api/v1/audit-log` (see
// `backend/app/api/v1/endpoints/audit_log.py`). Operators use this to
// answer "who did what to which lender / entry / dispute / invite,
// when". Lender_humans / debtors are 403'd server-side; the SPA also
// hides the nav link from non-operators to keep the chrome tidy.
//
// `extra` is a free-form bag of action-specific payload fields (note,
// reason, document_count, actor_role, etc.) that the writer dropped
// onto the row but the schema doesn't promote to top-level. The page
// surfaces these in a "details" disclosure where useful.

/**
 * One audit-log row. Top-level reference fields (`entry_id` /
 * `lender_id` / `dispute_id` / `invite_id` / `subject_key` /
 * `api_key_id` / `actor_user_id`) are present when the writer tagged
 * the row; everything else lives in `extra`. The wire-shape mirrors
 * `AuditLogEntry` in `backend/app/schemas/audit_log.py`.
 */
export interface AuditLogEntry {
  id: string;
  action: string;
  at: string;
  actor_user_id: string | null;
  /** Resolved server-side: operator name / e-mail or lender display name. */
  actor_label?: string | null;
  /** Admin "log ind som" mark. A row written while an administrator was
   *  logged in as the actor is stamped with it, and `actor_*` above is then
   *  the user they acted as. The first four are sent to operators only
   *  (null for lenders, debtors and partners, who learn that an
   *  administrator acted and never which one). `impersonator_label` is the
   *  administrator's name, else e-mail. Absent on older responses. */
  impersonator_user_id?: string | null;
  impersonation_id?: string | null;
  impersonator_label?: string | null;
  impersonated_role?: string | null;
  /** True on a stamped row that records money or consent (shown as
   *  ADMIN-INDGREB), for every reader. A name change, a note or a revoked
   *  key is stamped and traced but leaves this false. */
  admin_intervention?: boolean;
  entry_id: string | null;
  dispute_id: string | null;
  invite_id: string | null;
  lender_id: string | null;
  subject_key: string | null;
  api_key_id: string | null;
  /** Free-form, action-specific fields the writer dropped on the row
   *  (e.g. `note`, `reason`, `document_count`). Keys are whatever the
   *  writer chose; values are JSON-serialisable. */
  extra: Record<string, unknown>;
}

/** Filter + pagination payload for `listAuditLog`. Every field is
 *  optional; the backend defaults to no filter + page 1 / page_size 50. */
export interface AuditLogQuery {
  /** Exact action match, OR prefix match if the value ends with `.*`
   *  (e.g. `register.*` matches every `register.<verb>` row). */
  action?: string;
  entry_id?: string;
  lender_id?: string;
  dispute_id?: string;
  subject_key?: string;
  /** Inclusive lower bound on `at`. ISO datetime — the SPA passes
   *  `YYYY-MM-DD` strings from the date inputs and the backend
   *  parses them as midnight UTC. */
  from?: string;
  /** Inclusive upper bound on `at`. */
  to?: string;
  page?: number;
  page_size?: number;
}

export interface AuditLogListResponse {
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

/**
 * Operator-only paginated read of the audit log. Newest rows first.
 *
 * Lender_humans / debtors are 403'd server-side via `require_operator`
 * — the calling page also gates rendering on `user.role === 'operator'`
 * so the request never fires for the wrong role.
 */
export async function listAuditLog(
  query: AuditLogQuery,
): Promise<AuditLogListResponse> {
  // Filter out empty / null params so the URL doesn't carry useless
  // `entry_id=&lender_id=` noise — the backend would treat empty
  // strings as "match nothing" and return zero rows, which is worse
  // than no filter at all.
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    params[key] = value;
  }
  const { data } = await api.get<AuditLogListResponse>('/audit-log', {
    params,
  });
  return data;
}
